import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  OnInit,
  NgZone,
} from '@angular/core';

import { ConfigurationManagerService } from '../../../core/services/configuration-manager.service';
import { FLOOR_LEAFLET_TILE_OPACITY } from '../floor-leaflet.constants';
import L from 'leaflet';
// Force Leaflet to disable 3D CSS transforms (panning via translate3d).
// This forces Leaflet to pan using standard left/top properties instead, preventing
// .leaflet-map-pane from establishing a CSS stacking context. This is crucial for
// allowing tooltips, popups, and markers to render above Three.js/Babylon canvases.
if (L && L.Browser) {
  (L.Browser as any).any3d = false;
}
import 'leaflet.markercluster';
// @ts-ignore
import 'leaflet-imageoverlay-rotated';
import { DataContextService } from '../../../core/services/data-context.service';
import _ from 'lodash';
import { FloorService } from '../../../core/services/floor.service';
import { interval, Subject, Subscription, takeUntil } from 'rxjs';
import {
  FloorUserDevice,
  FloorUserResponse,
  FloorUsersRequest,
} from '../../../core/models/floor-user.model';
import { DataService } from '../../../core/services/data.service';
import * as d3 from 'd3';
import { GlobalSettingsService } from '../../../core/services/global-settings.service';
import * as Cesium from 'cesium';
import { SessionOptionsService } from '../../../core/services/session-option.service';
import { CameraService } from '../../../core/services/camera.service';
import { CoresService } from '../../../core/services/cores.service';
import 'leaflet-easybutton';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserClusterListUiComponent } from '../user-cluster-list-ui/user-cluster-list-ui.component';
import { OnDestroy, AfterViewInit } from '@angular/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ArcAngelPapiService } from '../../../core/services/arc-angel-papi.service';
import { BuildingModelAssetBundle } from '../../../core/models/building-model-assets.model';
import { BlenderFloorMapService } from '../../../core/services/blender-floor-map.service';

@Component({
  selector: 'app-floor-map',
  standalone: true,
  imports: [],
  templateUrl: './floor-map.component.html',
  styleUrl: './floor-map.component.css',
})
export class FloorMapComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  isLoading: boolean = false;
  @Input() dangerChecked: boolean = false;
  @Input() safeChecked: boolean = false;
  @Input() unknownChecked: boolean = false;
  @Input() suspectChecked: boolean = false;
  @Input() show3DUserClusters: boolean = true;
  /** When set, skips `BuildingModelAssetsService` and uses this bundle (GLB only). */
  @Input() assetBundle: BuildingModelAssetBundle | null = null;
  /** Passed to asset service when `assetBundle` is null (e.g. perimeter / facility id) */
  @Input() assetResolutionKey: string | null = null;

  safeIcon = L.icon({
    iconUrl: 'assets/img/arcangel/safe_user.png',
    iconSize: [32, 32],
  });
  unsafeIcon = L.icon({
    iconUrl: 'assets/img/arcangel/unsafe_user.png',
    iconSize: [32, 32],
  });
  unknownIcon = L.icon({
    iconUrl: 'assets/img/arcangel/unknown_user.png',
    iconSize: [32, 32],
  });
  cameraIcon = L.icon({
    iconUrl: 'assets/img/arcangel/camera-icon.png',
    iconSize: [24, 24],
  });
  accessControlIcon = L.icon({
    iconUrl: 'assets/img/arcangel/lock-48.png',
    iconSize: [24, 24],
  });
  raspberryPiIcon = L.icon({
    iconUrl: 'assets/img/arcangel/raspberry-pi.png',
    iconSize: [24, 24],
  });
  bluetoothIcon = L.icon({
    iconUrl: 'assets/img/arcangel/bluetooth.png',
    iconSize: [24, 24],
  });
  emsIcon = L.icon({
    iconUrl: 'assets/img/arcangel/ParamedicBadgeIcon.png',
    iconSize: [32, 32],
  });
  fireIcon = L.icon({
    iconUrl: 'assets/img/arcangel/FireBadgeIcon.png',
    iconSize: [32, 32],
  });
  policeIcon = L.icon({
    iconUrl: 'assets/img/arcangel/PoliceBadgeIcon.png',
    iconSize: [32, 32],
  });
  travelerIcon = L.icon({
    iconUrl: 'assets/img/arcangel/traveler_user.png',
    iconSize: [32, 32],
  });
  otherIcon = L.icon({
    iconUrl: 'assets/img/arcangel/fr_other.png',
    iconSize: [32, 32],
  });
  repairIcon = L.icon({
    iconUrl: 'assets/img/alert/support.png',
    iconSize: [18, 18],
  });
  @ViewChild('floorMap', { static: true })
  floorMapElement!: ElementRef<HTMLDivElement>;
  floorMap: L.Map | undefined;
  private floorPlanOverlay: any = null;
  private floorPlanOverlayVisible: boolean = true;

  /** Snapshot of the live Leaflet view — used to sync the fullscreen 3D modal map. */
  getLeafletViewSnapshot(): { lat: number; lng: number; zoom: number } | null {
    if (!this.floorMap) {
      return null;
    }
    const center = this.floorMap.getCenter();
    return { lat: center.lat, lng: center.lng, zoom: this.floorMap.getZoom() };
  }

  refreshLeafletMapSize(): void {
    this.floorMap?.invalidateSize();
  }
  setFloorPlanOverlayVisible(visible: boolean): void {
    this.floorPlanOverlayVisible = visible;
    if (!visible && this.floorPlanOverlay && this.floorMap) {
      this.floorMap.removeLayer(this.floorPlanOverlay);
    } else if (visible && this.floorPlanOverlay && this.floorMap) {
      this.floorPlanOverlay.addTo(this.floorMap);
    }
  }

  @Input() floorMapEntity: any = {};
  private threeJsInitialized: boolean = false;
  private threeScene: THREE.Scene | null = null;
  private threeRenderer: THREE.WebGLRenderer | null = null;
  private threeCamera: THREE.OrthographicCamera | THREE.PerspectiveCamera | null = null;
  private threeControls: OrbitControls | null = null;
  private threeRaycaster: THREE.Raycaster | null = null; // For hover detection
  private animationFrameId: number | null = null;
  private roomTooltip: L.Tooltip | null = null; // For displaying room labels
  private lastClickedRoomId: string | null = null; // Track last clicked room for debounce
  private lastClickTime: number = 0; // Track last click time for debounce
  private leafletSyncListeners: { [key: string]: () => void } = {}; // Store Leaflet event listeners for cleanup
  private isMapDragging: boolean = false; // Track if map is being dragged
  private readonly BEACON_SPRITE_SIZE = 15; // Fixed size in pixels for all beacon icons (consistent across all beacons)
  private readonly USER_COUNT_BADGE_SIZE = 30; // Fixed size in pixels for user count badges
  perimeterLayers: any = {};
  marker: any = {};
  beaconLayers: L.Marker[] = [];
  activeTab = 'user';
  private dataSubscription: Subscription | undefined;
  data = {
    users: [],
    userList1: [],
    userList2: [],
    userList3: [],
    dangerChecked: true,
    safeChecked: true,
    unknownChecked: true,
    show3DUserClusters: true,
    suspectChecked: true,
    limit: 30,
    page: 1,
    lastPage: 1,
    threshold: 250,
    gridSize: 50,
  };
  private destroy$ = new Subject<void>();
  private pollerSubscription?: Subscription;
  floorUserDevice: FloorUserDevice[] = [];
  private lastUserDataForClustering: any = null; // Store last user data for re-clustering on zoom
  dataIsCluster = false;
  leafletFloorHeight: string | undefined;
  tabHeight: string | undefined;
  clusterMarkers: any;
  @Output() numberOfBeaconsChange = new EventEmitter<number>();
  @Output() numberOfCamerasChange = new EventEmitter<number>();
  @Output() userOnFloorsChange = new EventEmitter<any>();
  @Output() cameraLayerChange = new EventEmitter<any>();
  @Output() updateChanges = new EventEmitter<any>();
  @Output() perimeterChange = new EventEmitter<any>();
  @Output() userClicked = new EventEmitter<{ userId: string; roomId: string; mesh: any }>();
  @Output() floorUserDevicesRaw = new EventEmitter<any[]>();
  @Input() showBeacons = false;
  clusterFirstResponders: boolean = false;
  firstResponderMarkers: any;
  trackingDeviceMarkers: any;
  numberOfCameras: number = 0;
  cameraLayer: any[] = [];
  @Input() showCameraLayer: boolean = true;
  numberOfAccessControls: number = 0;
  accessControlLayers: L.Marker[] = [];
  accessControlListForFloor: any[] = [];
  @Input() showAccessControlLayer: boolean = false;
  @Output() numberOfAccessControlsChange = new EventEmitter<number>();
  @Output() accessControlLayerChange = new EventEmitter<any>();
  numberOfRaspberryPis: number = 0;
  raspberryPiLayers: L.Marker[] = [];
  raspberryPiListForFloor: any[] = [];
  @Input() showRaspberryPiLayer: boolean = false;
  @Input() raspberryPiDevices: any[] = [];
  @Input() raspberryPiDeviceCount: number = 0;
  @Input() raspberryPiSensorCounts: { [key: string]: number } = {};
  @Input() showDeviceMarkers: boolean = false;
  @Output() numberOfRaspberryPisChange = new EventEmitter<number>();
  @Output() raspberryPiLayerChange = new EventEmitter<any>();
  @Output() raspberryPiMarkerClick = new EventEmitter<any>();
  @Output() deviceMarkerClick = new EventEmitter<any>();
  @Output() numberOfDevicesWithCoordsChange = new EventEmitter<number>();
  deviceMarkers: Map<string | number, L.Marker> = new Map();
  deviceClusterGroup: any = null;
  selectedDeviceId: string | number | null = null;
  numberOfDevicesWithCoords: number = 0;
  /** When true, hide the Three.js 2D floor overlay but keep the Leaflet tile layer. */
  @Input() suppressThreeOverlay = false;
  incidentUpdateMarkers: any[] = [];
  updates: any[] | undefined;
  enablePlayback: boolean = false;
  enableMonitor: boolean | undefined;
  enableLiveMode: boolean | undefined;
  fireMarkerLayer: any;
  dataSourceUserIn3D: any | undefined;
  viewer: any = undefined;
  last3DUserData: any = undefined;
  stopPoller: boolean | undefined;
  isCollapsed = false;
  @Output() collapseChange = new EventEmitter<boolean>();
  private userClusterModalRef?: import('@ng-bootstrap/ng-bootstrap').NgbModalRef;
  private handlerLeftClick: Cesium.ScreenSpaceEventHandler | undefined;
  private cameraLocked = false;
  private isLeafletMoving = false;
  private roomColorMap = new Map<string | number, THREE.Color>();
  private hoveredRoomId: string | number | null = null;
  private static globalRenderer: THREE.WebGLRenderer | null = null;

  constructor(
    private dataContextService: DataContextService,
    private floorService: FloorService,
    private dataService: DataService,
    private globalSettingsService: GlobalSettingsService,
    private sessionOptionService: SessionOptionsService,
    private cameraService: CameraService,
    private coresService: CoresService,
    private modalService: NgbModal,
    private arcAngelPapiService: ArcAngelPapiService,
    private blenderFloorMap: BlenderFloorMapService,
    private ngZone: NgZone
  ) { }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suppressThreeOverlay']) {
      this.applyThreeOverlayVisibility();
    }
    if (changes['show3DUserClusters']) {
      this.data.show3DUserClusters = changes['show3DUserClusters'].currentValue;
      this.getFloorUserInBBoxAsCluster();
    }
    if (
      (changes['dangerChecked'] && !changes['dangerChecked'].firstChange) ||
      (changes['safeChecked'] && !changes['safeChecked'].firstChange) ||
      (changes['unknownChecked'] && !changes['unknownChecked'].firstChange) ||
      (changes['suspectChecked'] && !changes['suspectChecked'].firstChange)
    ) {
      this.onStatusChanged();
      this.createClusterMarkers();
    }

    // Initialize Three.js when floorMapEntity changes
    if (changes['floorMapEntity'] && !changes['floorMapEntity'].firstChange) {
      this.isLoading = true;
      // Clear old user data when floor changes
      this.floorUserDevice = [];
      this.lastUserDataForClustering = null;
      // Clear Three.js users before re-initializing
      this.clearUsersInThreeJs();
      this.updateFloorMapView();
      this.initializeThreeJsFloorMap();
    }

    if (changes['showBeacons'] && !changes['showBeacons'].firstChange) {
      this.beaconOnChange();
    }
    if (changes['showCameraLayer'] && !changes['showCameraLayer'].firstChange) {
      this.cameraOnChange();
    }
    if (changes['showAccessControlLayer'] && !changes['showAccessControlLayer'].firstChange) {
      this.accessControlOnChange();
    }
    if (changes['showRaspberryPiLayer']) {
      this.raspberryPiOnChange();
    }
    if (changes['showDeviceMarkers']) {
      this.deviceMarkersOnChange();
    }
    if (changes['raspberryPiDevices'] || changes['raspberryPiDeviceCount'] || changes['raspberryPiSensorCounts']) {
      this.updateRaspberryPiMarkers();
      this.updateDeviceLocationMarkers();
    }
  }

  getMarkerDeviceCount(marker: any): number {
    const totalCount = this.raspberryPiDeviceCount != null && this.raspberryPiDeviceCount !== undefined
      ? this.raspberryPiDeviceCount
      : (this.raspberryPiDevices ? this.raspberryPiDevices.length : 0);

    if (this.raspberryPiSensorCounts && Object.keys(this.raspberryPiSensorCounts).length > 0) {
      const sensorUuid = (marker?.sensorUuid || marker?.sensor?.uuid || marker?.sensor?.raspberryPi?.uuid || '').toString().trim();
      const sensorId = marker?.sensorId != null ? String(marker.sensorId) : (marker?.sensor?.id != null ? String(marker.sensor.id) : '');
      const rpiId = marker?.raspberryPiId != null ? String(marker.raspberryPiId) : (marker?.sensor?.raspberryPiId != null ? String(marker.sensor.raspberryPiId) : '');
      const perimeterId = marker?.perimeterId != null ? String(marker.perimeterId) : (marker?.sensor?.perimeterId != null ? String(marker.sensor.perimeterId) : '');

      if (sensorUuid && this.raspberryPiSensorCounts[sensorUuid] !== undefined) {
        return this.raspberryPiSensorCounts[sensorUuid];
      }
      if (sensorUuid) {
        const lowerUuid = sensorUuid.toLowerCase();
        const matchingKey = Object.keys(this.raspberryPiSensorCounts).find(k => k.toLowerCase() === lowerUuid);
        if (matchingKey && this.raspberryPiSensorCounts[matchingKey] !== undefined) {
          return this.raspberryPiSensorCounts[matchingKey];
        }
      }

      if (sensorId && this.raspberryPiSensorCounts[sensorId] !== undefined) {
        return this.raspberryPiSensorCounts[sensorId];
      }
      if (rpiId && this.raspberryPiSensorCounts[rpiId] !== undefined) {
        return this.raspberryPiSensorCounts[rpiId];
      }
      if (perimeterId && this.raspberryPiSensorCounts[perimeterId] !== undefined) {
        return this.raspberryPiSensorCounts[perimeterId];
      }
    }

    if (this.raspberryPiLayers && this.raspberryPiLayers.length === 1) {
      return totalCount;
    }

    return 0;
  }

  createRaspberryPiDivIcon(count: number = 0, label?: string): L.DivIcon {
    const displayCount = count > 999 ? '999+' : count.toString();
    const tooltipText = label ? `${label} (${count} active)` : `Raspberry Pi (${count} active)`;
    const html = `
      <div class="rpi-marker-wrapper" title="${tooltipText}">
        <img src="assets/img/arcangel/raspberry-pi.png" class="rpi-marker-img" alt="Raspberry Pi" />
        <div class="rpi-marker-badge">${displayCount}</div>
      </div>
    `;

    return L.divIcon({
      html: html,
      className: 'rpi-leaflet-div-icon',
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -14],
      tooltipAnchor: [0, -14]
    });
  }

  updateRaspberryPiMarkers() {
    _.forEach(this.raspberryPiLayers, (marker: any) => {
      const count = this.getMarkerDeviceCount(marker);
      marker.deviceCount = count;
      const markerDisplay = count > 999 ? '999+' : count.toString();
      const label = marker.sensorLabel || marker.label || 'Raspberry Pi';

      const el = marker.getElement ? marker.getElement() : null;
      if (el) {
        const badgeEl = el.querySelector('.rpi-marker-badge');
        if (badgeEl) {
          badgeEl.textContent = markerDisplay;
        }
        const wrapperEl = el.querySelector('.rpi-marker-wrapper') || el;
        wrapperEl.setAttribute('title', `${label} (${count} active)`);
      } else {
        marker.setIcon(this.createRaspberryPiDivIcon(count, label));
      }
    });
  }

  beaconOnChange() {
    if (this.showBeacons) {
      // Clear room tooltip when beacons are turned on to prevent overlaps
      this.clearRoomTooltip();
      this.hoveredRoomId = null;

      // Render beacons in Three.js floor only
      // Check for actual Three.js objects rather than initialization flag
      if (this.threeScene && this.threeCamera && this.threeRenderer) {
        // Hide Leaflet beacon markers to avoid double icons when rendering in Three.js
        this.hideBeaconLayerOnMap();
        this.renderBeaconsInThreeJs();
      } else {
        // Show beacons on Leaflet map if Three.js is not active
        this.showBeaconLayerOnMap();
      }
    } else {
      // Hide from both Leaflet and Three.js
      this.hideBeaconLayerOnMap();

      // Remove beacons from Three.js
      if (this.threeScene) {
        const beaconsToRemove: THREE.Object3D[] = [];
        this.threeScene.traverse((object) => {
          if (object.userData['isBeacon']) {
            beaconsToRemove.push(object);
          }
        });
        beaconsToRemove.forEach(obj => {
          if (obj instanceof THREE.Sprite) {
            if (obj.material instanceof THREE.SpriteMaterial && obj.material.map) {
              obj.material.map.dispose();
            }
            if (obj.material) {
              obj.material.dispose();
            }
          }
          if (this.threeScene) {
            this.threeScene.remove(obj);
          }
        });
      }
    }
  }
  cameraOnChange() {
    if (!this.floorMap) return;
    if (this.showCameraLayer) {
      _.forEach(this.cameraLayer, (layer) => {
        layer.addTo(this.floorMap!);
      });
    } else {
      _.forEach(this.cameraLayer, (layer) => {
        this.floorMap!.removeLayer(layer);
      });
    }
  }
  accessControlOnChange() {
    if (!this.floorMap) return;
    if (this.showAccessControlLayer) {
      _.forEach(this.accessControlLayers, (layer) => {
        layer.addTo(this.floorMap!);
      });
    } else {
      _.forEach(this.accessControlLayers, (layer) => {
        this.floorMap!.removeLayer(layer);
      });
    }
  }
  raspberryPiOnChange() {
    if (!this.floorMap) return;
    if (this.showRaspberryPiLayer) {
      if (this.raspberryPiLayers.length === 0) {
        this.loadCamerasOnMap();
      }
      this.updateRaspberryPiMarkers();
      _.forEach(this.raspberryPiLayers, (layer) => {
        if (!this.floorMap!.hasLayer(layer)) {
          layer.addTo(this.floorMap!);
        }
      });
    } else {
      _.forEach(this.raspberryPiLayers, (layer) => {
        if (this.floorMap!.hasLayer(layer)) {
          this.floorMap!.removeLayer(layer);
        }
      });
    }
  }

  initDeviceClusterGroup(): void {
    if (!this.floorMap) return;
    if (!this.deviceClusterGroup) {
      if (typeof (L as any).markerClusterGroup === 'function') {
        this.deviceClusterGroup = (L as any).markerClusterGroup({
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          spiderfyOnMaxZoom: true,
          disableClusteringAtZoom: 19,
          maxClusterRadius: 35,
          clusterPane: this.floorMap?.getPane('device-pane') ? 'device-pane' : undefined,
          iconCreateFunction: (cluster: any) => this.createDeviceClusterIcon(cluster),
        });
      } else {
        this.deviceClusterGroup = L.layerGroup();
      }
    }
  }

  deviceMarkersOnChange(): void {
    if (!this.floorMap) return;
    this.initDeviceClusterGroup();
    const shouldShow = this.showDeviceMarkers;
    if (shouldShow) {
      this.updateDeviceLocationMarkers();
      if (this.deviceClusterGroup && !this.floorMap.hasLayer(this.deviceClusterGroup)) {
        this.floorMap.addLayer(this.deviceClusterGroup);
      }
    } else {
      if (this.deviceClusterGroup && this.floorMap.hasLayer(this.deviceClusterGroup)) {
        this.floorMap.removeLayer(this.deviceClusterGroup);
      }
    }
  }

  parseDeviceCoordinates(device: any): [number, number] | null {
    if (!device) return null;

    let lat: number | null = null;
    let lng: number | null = null;

    // 1. Array format device.coordinates: [lat, lng] or [lng, lat]
    if (Array.isArray(device.coordinates) && device.coordinates.length >= 2) {
      const v0 = Number(device.coordinates[0]);
      const v1 = Number(device.coordinates[1]);
      if (!isNaN(v0) && !isNaN(v1)) {
        if (Math.abs(v0) > 90) {
          lat = v1;
          lng = v0;
        } else if (Math.abs(v1) > 90) {
          lat = v0;
          lng = v1;
        } else {
          const centerLat = this.floorMapEntity?.centerpoint?.latitude;
          const centerLng = this.floorMapEntity?.centerpoint?.longitude;
          if (centerLat != null && centerLng != null) {
            const distNormal = Math.hypot(v0 - centerLat, v1 - centerLng);
            const distSwapped = Math.hypot(v1 - centerLat, v0 - centerLng);
            if (distSwapped < distNormal) {
              lat = v1;
              lng = v0;
            } else {
              lat = v0;
              lng = v1;
            }
          } else {
            lat = v0;
            lng = v1;
          }
        }
      }
    } else if (device.position?.coordinates && Array.isArray(device.position.coordinates) && device.position.coordinates.length >= 2) {
      const v0 = Number(device.position.coordinates[0]);
      const v1 = Number(device.position.coordinates[1]);
      if (!isNaN(v0) && !isNaN(v1)) {
        lat = v1;
        lng = v0;
      }
    } else if (device.geometry?.coordinates && Array.isArray(device.geometry.coordinates) && device.geometry.coordinates.length >= 2) {
      const v0 = Number(device.geometry.coordinates[0]);
      const v1 = Number(device.geometry.coordinates[1]);
      if (!isNaN(v0) && !isNaN(v1)) {
        lat = v1;
        lng = v0;
      }
    } else if (device.latitude != null && device.longitude != null) {
      lat = Number(device.latitude);
      lng = Number(device.longitude);
    } else if (device.lat != null && device.lng != null) {
      lat = Number(device.lat);
      lng = Number(device.lng);
    }

    if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return [lat, lng];
    }
    return null;
  }

  updateDeviceLocationMarkers(): void {
    if (!this.floorMap) return;
    this.initDeviceClusterGroup();

    if (this.deviceClusterGroup) {
      this.deviceClusterGroup.clearLayers();
    }
    this.deviceMarkers.clear();

    const devices = this.raspberryPiDevices || [];
    if (devices.length === 0) {
      this.numberOfDevicesWithCoords = 0;
      this.numberOfDevicesWithCoordsChange.emit(0);
      return;
    }

    let countWithCoords = 0;

    devices.forEach((device: any, index: number) => {
      const coords = this.parseDeviceCoordinates(device);
      if (!coords) return;

      const [lat, lng] = coords;
      countWithCoords++;

      const marker = this.createDeviceMarker(device, lat, lng, index);
      const key = device.id || device.externalDeviceId || `dev-${index}`;
      this.deviceMarkers.set(key, marker);

      if (this.deviceClusterGroup) {
        this.deviceClusterGroup.addLayer(marker);
      }
    });

    this.numberOfDevicesWithCoords = countWithCoords;
    this.numberOfDevicesWithCoordsChange.emit(countWithCoords);

    const shouldShow = this.showDeviceMarkers;
    if (shouldShow && this.deviceClusterGroup && !this.floorMap.hasLayer(this.deviceClusterGroup)) {
      this.floorMap.addLayer(this.deviceClusterGroup);
    }
  }

  createDeviceMarker(device: any, lat: number, lng: number, index: number): L.Marker {
    const isBle = (device.transport || device.serviceType || 'BLE').toUpperCase().includes('BLE');
    const isWifi = (device.transport || '').toUpperCase().includes('WIFI');
    const platformLower = (device.platform || '').toLowerCase();

    let platformIcon = isWifi ? 'fa-solid fa-wifi' : 'fa-brands fa-bluetooth-b';
    if (platformLower.includes('ios') || platformLower.includes('apple')) {
      platformIcon = 'fa-brands fa-apple';
    } else if (platformLower.includes('android')) {
      platformIcon = 'fa-brands fa-android';
    } else if (platformLower.includes('windows')) {
      platformIcon = 'fa-brands fa-windows';
    } else if (platformLower.includes('linux')) {
      platformIcon = 'fa-brands fa-linux';
    }

    const rssi = device.rssi != null && !isNaN(Number(device.rssi)) ? Number(device.rssi) : null;
    let signalClass = 'signal-unknown';
    if (rssi !== null) {
      if (rssi >= -65) signalClass = 'signal-strong';
      else if (rssi >= -80) signalClass = 'signal-medium';
      else signalClass = 'signal-weak';
    }

    const typeClass = isWifi ? 'device-wifi' : 'device-ble';
    const name = device.device_name || device.name || device.externalDeviceId || 'Device';
    const safeName = this.escapeHtml(name);

    const html = `
      <div class="device-marker-wrapper ${typeClass} ${signalClass}" title="${safeName}">
        <div class="device-pulse-ring"></div>
        <div class="device-marker-pin">
          <i class="${platformIcon}"></i>
          ${rssi !== null ? `<span class="device-rssi-dot" title="${rssi} dBm"></span>` : ''}
        </div>
      </div>
    `;

    const icon = L.divIcon({
      html: html,
      className: 'device-leaflet-div-icon',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -16],
      tooltipAnchor: [0, -16]
    });

    const marker = L.marker([lat, lng], {
      icon: icon,
      pane: this.floorMap?.getPane('device-pane') ? 'device-pane' : undefined,
      zIndexOffset: 800
    });

    (marker as any).deviceData = device;
    (marker as any).deviceId = device.id;

    // Tooltip
    const tooltipHtml = `
      <div class="device-map-tooltip-content">
        <div class="device-tooltip-name">${safeName}</div>
        <div class="device-tooltip-sub">
          <span class="badge ${isWifi ? 'bg-success' : 'bg-primary'}">${isWifi ? 'WiFi' : 'BLE'}</span>
          <span class="badge bg-secondary">${this.escapeHtml(device.platform || 'Other')}</span>
          ${rssi !== null ? `<span class="badge bg-dark">${rssi} dBm</span>` : ''}
        </div>
      </div>
    `;
    marker.bindTooltip(tooltipHtml, {
      className: 'device-leaflet-tooltip',
      direction: 'top',
      offset: [0, -14]
    });

    // Rich popup
    const popupHtml = this.createDevicePopupHtml(device, lat, lng, isWifi, platformIcon, rssi, signalClass);
    marker.bindPopup(popupHtml, {
      className: 'device-leaflet-popup-card',
      maxWidth: 320,
      minWidth: 260
    });

    marker.on('click', () => {
      this.selectedDeviceId = device.id;
      this.ngZone.run(() => {
        this.deviceMarkerClick.emit(device);
      });
    });

    return marker;
  }

  createDeviceClusterIcon(cluster: any): L.DivIcon {
    const count = cluster.getChildCount();
    const markers = cluster.getAllChildMarkers();
    let bleCount = 0;
    let wifiCount = 0;
    markers.forEach((m: any) => {
      const transport = (m.deviceData?.transport || m.deviceData?.serviceType || '').toUpperCase();
      if (transport.includes('WIFI')) wifiCount++;
      else bleCount++;
    });

    const isDominantWifi = wifiCount > bleCount;
    const badgeClass = isDominantWifi ? 'cluster-wifi' : 'cluster-ble';
    const displayCount = count > 999 ? '999+' : count.toString();

    const html = `
      <div class="ble-cluster-pill ${badgeClass}" title="${count} devices nearby (${bleCount} BLE, ${wifiCount} WiFi)">
        <span class="cluster-pulse-ring"></span>
        <div class="cluster-inner">
          <i class="${isDominantWifi ? 'fa-solid fa-wifi' : 'fa-brands fa-bluetooth-b'}"></i>
          <span class="cluster-num">${displayCount}</span>
        </div>
      </div>
    `;

    return L.divIcon({
      html: html,
      className: 'ble-cluster-leaflet-div',
      iconSize: [44, 28],
      iconAnchor: [22, 14]
    });
  }

  private createDevicePopupHtml(
    device: any,
    lat: number,
    lng: number,
    isWifi: boolean,
    platformIcon: string,
    rssi: number | null,
    signalClass: string
  ): string {
    const name = device.device_name || device.name || device.externalDeviceId || 'Device';
    const safeName = this.escapeHtml(name);
    const platform = this.escapeHtml(device.platform || 'Other');
    const transport = isWifi ? 'WiFi' : 'BLE';
    const mac = this.escapeHtml(device.advertisedMac || device.address || 'N/A');
    const externalId = device.externalDeviceId ? this.escapeHtml(device.externalDeviceId) : '';
    const perimeterId = device.perimeterId ? this.escapeHtml(String(device.perimeterId)) : '';
    const manufacturerId = device.manufacturerId || device.manufacturer_id ? this.escapeHtml(String(device.manufacturerId || device.manufacturer_id)) : '';
    const lastSeen = device.lastSeen || device.last_seen;
    const formattedLastSeen = lastSeen ? this.escapeHtml(new Date(lastSeen).toLocaleString()) : '';

    return `
      <div class="dev-popup-container">
        <div class="dev-popup-header">
          <div class="dev-popup-avatar ${isWifi ? 'avatar-wifi' : 'avatar-ble'}">
            <i class="${platformIcon}"></i>
          </div>
          <div class="dev-popup-title-area">
            <div class="dev-popup-name text-truncate" title="${safeName}">${safeName}</div>
            <div class="dev-popup-id">ID: #${this.escapeHtml(String(device.id || 'N/A'))}</div>
          </div>
        </div>

        <div class="dev-popup-pills">
          <span class="dev-pill ${isWifi ? 'pill-wifi' : 'pill-ble'}">
            <i class="${isWifi ? 'fa-solid fa-wifi' : 'fa-brands fa-bluetooth-b'} me-1"></i>${transport}
          </span>
          <span class="dev-pill pill-platform">${platform}</span>
          ${rssi !== null ? `
            <span class="dev-pill pill-rssi ${signalClass}">
              <i class="fa-solid fa-signal me-1"></i>${rssi} dBm
            </span>
          ` : ''}
        </div>

        <div class="dev-popup-details-grid">
          <div class="dev-grid-row">
            <span class="dev-label"><i class="fa-solid fa-location-dot me-1 text-danger"></i>Location:</span>
            <span class="dev-value font-monospace">${lat.toFixed(6)}, ${lng.toFixed(6)}</span>
          </div>
          ${mac !== 'N/A' ? `
            <div class="dev-grid-row">
              <span class="dev-label"><i class="fa-solid fa-network-wired me-1 text-info"></i>MAC:</span>
              <span class="dev-value font-monospace">${mac}</span>
            </div>
          ` : ''}
          ${externalId ? `
            <div class="dev-grid-row">
              <span class="dev-label"><i class="fa-solid fa-fingerprint me-1 text-warning"></i>UUID:</span>
              <span class="dev-value font-monospace text-truncate" title="${externalId}">${externalId}</span>
            </div>
          ` : ''}
          ${perimeterId ? `
            <div class="dev-grid-row">
              <span class="dev-label"><i class="fa-solid fa-vector-square me-1 text-success"></i>Perimeter:</span>
              <span class="dev-value">${perimeterId}</span>
            </div>
          ` : ''}
          ${manufacturerId ? `
            <div class="dev-grid-row">
              <span class="dev-label"><i class="fa-solid fa-microchip me-1 text-secondary"></i>Manufacturer:</span>
              <span class="dev-value">${manufacturerId}</span>
            </div>
          ` : ''}
          ${formattedLastSeen ? `
            <div class="dev-grid-row">
              <span class="dev-label"><i class="fa-regular fa-clock me-1 text-muted"></i>Last Seen:</span>
              <span class="dev-value">${formattedLastSeen}</span>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  focusDevice(deviceOrId: any): void {
    if (!this.floorMap) return;
    const id = typeof deviceOrId === 'object'
      ? (deviceOrId.id || deviceOrId.externalDeviceId)
      : deviceOrId;

    let marker = this.deviceMarkers.get(id);
    if (!marker && typeof deviceOrId === 'object') {
      for (const [key, m] of this.deviceMarkers.entries()) {
        const d = (m as any).deviceData;
        if (d && (d.id === deviceOrId.id || (deviceOrId.externalDeviceId && d.externalDeviceId === deviceOrId.externalDeviceId))) {
          marker = m;
          break;
        }
      }
    }

    let lat: number | null = null;
    let lng: number | null = null;

    if (marker) {
      const latLng = marker.getLatLng();
      lat = latLng.lat;
      lng = latLng.lng;
    } else if (typeof deviceOrId === 'object') {
      const coords = this.parseDeviceCoordinates(deviceOrId);
      if (coords) {
        lat = coords[0];
        lng = coords[1];
      }
    }

    if (lat !== null && lng !== null) {
      if (!this.showDeviceMarkers) {
        this.showDeviceMarkers = true;
        this.deviceMarkersOnChange();
      }

      const map = this.floorMap;
      this.ngZone.runOutsideAngular(() => {
        const zoom = Math.min(Math.max(map.getZoom(), 18), 19);
        map.setView([lat, lng], zoom, { animate: true });

        if (marker) {
          if (this.deviceClusterGroup && typeof this.deviceClusterGroup.zoomToShowLayer === 'function') {
            this.deviceClusterGroup.zoomToShowLayer(marker, () => {
              marker!.openPopup();
            });
          } else {
            marker.openPopup();
          }
        }
      });
    }
  }

  private escapeHtml(str: string): string {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  callRaspberryPiUrlsForFloor() {
    if (!this.raspberryPiListForFloor || this.raspberryPiListForFloor.length === 0) {
      return;
    }
    _.forEach(this.raspberryPiListForFloor, (sensor: any) => {
      if (sensor?.raspberryPi?.url) {
        this.callRaspberryPiUrl(sensor.raspberryPi.url, sensor.raspberryPi.protocol);
      } else if (sensor?.raspberryPiId) {
        this.dataService.getRaspberryPi(sensor.raspberryPiId).subscribe({
          next: (rpiData: any) => {
            sensor.raspberryPi = rpiData;
            if (rpiData?.url) {
              this.callRaspberryPiUrl(rpiData.url, rpiData.protocol);
            }
          },
          error: (err: any) => {
            console.error('Error fetching Raspberry Pi by ID:', sensor.raspberryPiId, err);
          }
        });
      } else if (sensor?.id) {
        this.dataService.findRaspberryPi({ where: { sensorId: sensor.id } }).subscribe({
          next: (rpiResult: any) => {
            const foundRpi = rpiResult?.rows?.find((r: any) => r.sensorId?.toString() === sensor.id?.toString());
            if (foundRpi && foundRpi.id) {
              sensor.raspberryPiId = foundRpi.id;
              this.dataService.getRaspberryPi(foundRpi.id).subscribe({
                next: (rpiData: any) => {
                  sensor.raspberryPi = rpiData;
                  if (rpiData?.url) {
                    this.callRaspberryPiUrl(rpiData.url, rpiData.protocol);
                  }
                }
              });
            }
          }
        });
      }
    });
  }

  private callRaspberryPiUrl(rawUrl: string, protocol?: string) {
    if (!rawUrl) return;
    let url = rawUrl;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      const proto = protocol ? protocol.replace(':', '') : 'http';
      url = `${proto}://${url}`;
    }
    this.dataService.callUrl(url).subscribe({
      next: (res: any) => {
        console.log('Called Raspberry Pi URL successfully:', url, res);
        let count = 0;
        if (res && res.data && Array.isArray(res.data)) {
          count = res.data.length;
        } else if (Array.isArray(res)) {
          count = res.length;
        }
        if (count > 0 || (res && (res.data || Array.isArray(res)))) {
          this.raspberryPiDeviceCount = count;
          this.updateRaspberryPiMarkers();
        }
      },
      error: (err: any) => {
        console.error('Error calling Raspberry Pi URL:', url, err);
      }
    });
  }
  viewCamera(camera: any) {
    // viewCamera click handler
  }
  ngOnInit(): void {
    this.enableLiveMode = this.sessionOptionService.isLiveMode();
    this.enableMonitor = this.sessionOptionService.isMonitorMode();
    this.enablePlayback = this.sessionOptionService.isPlaybackMode();

    this.sessionOptionService.mode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        if (mode === 'Monitor Mode') {
          this.closeUserClusterModal('monitor-mode-toggle');
        }
      });

    this.dataSubscription = this.floorService.floorData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((floor: any) => {
        this.isLoading = true;
        this.floorMapEntity = floor;
        if (this.pollerSubscription) {
          this.pollerSubscription.unsubscribe();
        }

        // Use different polling intervals for different modes
        const pollingInterval = this.enablePlayback ? 1000 :
          ConfigurationManagerService.configSettings.setIntervalForApiCall;

        this.pollerSubscription = interval(pollingInterval)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            if (this.enablePlayback) {
              this.pollerPlayback();
            } else {
              this.poller();
            }
            this.startTickListeners();
          });
        this.viewer = this.coresService.getViewer();
        this.createClusterMarkers();
        this.updateFloorMapView();
        // Initialize Three.js rendering for floor map
        this.initializeThreeJsFloorMap();
        // GLB loading disabled on initialization - only load via Show All Buildings button
        // this.resolveAndLoadGlbOnCesium();
      });
    setTimeout(() => {
      this.setUpClickAction();
    }, 500);
  }
  private setUpClickAction(): void {
    if (!this.viewer || this.handlerLeftClick) return;
    this.handlerLeftClick = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);
    this.handlerLeftClick.setInputAction((click: any) => {
      this.leftClickAction(click);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  private leftClickAction(click: any): void {
    if (!this.viewer) return;
    const pickedObjectAll = this.viewer.scene.drillPick(click.position);
    for (const obj of pickedObjectAll) {
      if (
        obj.id &&
        (obj.id.name === 'userClucterIn3D' || obj.id._name === 'userClucterIn3D')
      ) {
        let markers = this.marker;
        if (!Array.isArray(markers)) {
          markers = [markers];
        }
        const data: any = {};
        data.dataIsCluster = true;
        data.data = obj.id.data ?? obj.id._data;
        data.statuses = obj.id.statuses ?? obj.id._statuses;
        data.timestamp = obj.id.timestamp ?? obj.id._timestamp;
        data.floorPerimeterId =
          this.floorMapEntity.obj && this.floorMapEntity.obj.type === 'GEOFENCE'
            ? undefined
            : null;
        data.floorType =
          this.floorMapEntity.obj && this.floorMapEntity.obj.type === 'GEOFENCE'
            ? 'BOTH'
            : 'FLOOR';
        data.incidentId = this.floorMapEntity.eventId;
        data.entity = this.floorMapEntity;
        data.userData = markers.map((o: any) => o.userData);
        data.lat = obj.id.data.position ? obj.id.data.position.coordinates[1] : undefined;
        data.lon = obj.id.data.position ? obj.id.data.position.coordinates[0] : undefined;
        data.clusterSize = obj.id.data.clusterSize ?? obj.id.data._clusterSize;
        this.openUserClusterListUi(data);
        break;
      }
    }
  }
  createClusterMarkers() {
    setTimeout(() => {
      const windowId = this.floorMapEntity.windowId ?? 0;
      const windowHeight = 400; //WindowService.getHeightOfWindowSpace(windowId);
      const headerHeight =
        document.getElementById('floor-vis-header')?.offsetHeight ?? 0;
      const height = windowHeight - headerHeight - 50;

      this.leafletFloorHeight = 0.5 * height + 'px';
      this.tabHeight = 0.5 * height + 'px';

      this.clusterMarkers = L.markerClusterGroup({
        iconCreateFunction: this.defineClusterIcon.bind(this),
        maxClusterRadius: 1,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        spiderfyOnMaxZoom: false,
        singleMarkerMode: true,
      });

      this.clusterMarkers.on('click', (e: any) => {
        const dataIsCluster = e.layer.dataIsCluster;
        const data = dataIsCluster ? e.layer.data : null;
        const statuses = dataIsCluster ? e.layer.statuses : [];
        let userData: any[] = [];
        if (!dataIsCluster) {
          userData = [e.layer.userData];
        }
        userData = _.sortBy(userData, 'name');
        this.openUserClusterListUi({
          dataIsCluster,
          data,
          statuses,
          userData,
          incidentId: this.floorMapEntity.eventId,
          entity: this.floorMapEntity,
          lat: dataIsCluster ? data.position.coordinates[1] : undefined,
          lon: dataIsCluster ? data.position.coordinates[0] : undefined,
          clusterSize: dataIsCluster ? data.clusterSize : undefined,
          timestamp: dataIsCluster ? data.timestamp : undefined,
        });
      });

      this.clusterMarkers.on('clusterclick', (e: any) => {
        const markers = e.layer.getAllChildMarkers();
        const dataIsCluster = markers[0].dataIsCluster;
        const data = dataIsCluster ? markers[0].data : null;
        const statuses = dataIsCluster ? markers[0].statuses : [];
        let userData: any[] = [];
        if (!dataIsCluster) {
          userData = markers.map((o: any) => o.userData);
          userData = _.sortBy(userData, 'name');
        }
        this.openUserClusterListUi({
          dataIsCluster,
          data,
          statuses,
          userData,
          incidentId: this.floorMapEntity.eventId,
          entity: this.floorMapEntity,
          lat: dataIsCluster ? data.position.coordinates[1] : undefined,
          lon: dataIsCluster ? data.position.coordinates[0] : undefined,
          clusterSize: dataIsCluster ? data.clusterSize : undefined,
          timestamp: dataIsCluster ? data.timestamp : undefined,
        });
      });
      /////////// ****************** To Do for cluster Responders
      if (this.clusterFirstResponders) {
        this.firstResponderMarkers = L.markerClusterGroup({
          iconCreateFunction: this.defineClusterIcon,
        });
      } else {
        this.firstResponderMarkers = L.layerGroup();
      }

      this.trackingDeviceMarkers = L.markerClusterGroup({
        iconCreateFunction: this.defineClusterIcon,
      });
      // Hide Leaflet cluster markers - comment out addLayer to prevent them from showing
      // Keep clusterMarkers for data structure but don't add to map
      // if (this.floorMap) {
      //   this.floorMap.addLayer(this.clusterMarkers);
      // }

      // EasyButton for expand/compress
      // const toggle = L.easyButton({
      //   states: [{
      //     icon: 'fa-expand',
      //     stateName: 'expand',
      //     title: 'Expand',
      //     onClick: (control: any) => {
      //       const windowHeight = WindowService.getHeightOfWindowSpace(windowId);
      //       const headerHeight = (document.getElementById('floor-vis-header')?.offsetHeight ?? 0);
      //       const height = windowHeight - headerHeight - 50;
      //       this.leafletFloorHeight = height + 'px';
      //       this.expandLeaflet = true;
      //       control.state('compress');
      //       this.floorMap.invalidateSize();
      //     }
      //   }, {
      //     icon: 'fa-compress',
      //     stateName: 'compress',
      //     title: 'Compress',
      //     onClick: (control: any) => {
      //       const windowHeight = WindowService.getHeightOfWindowSpace(windowId);
      //       const headerHeight = (document.getElementById('floor-vis-header')?.offsetHeight ?? 0);
      //       const height = windowHeight - headerHeight - 50;
      //       this.leafletFloorHeight = (0.5 * height) + 'px';
      //       this.tabHeight = (0.5 * height) + 'px';
      //       this.expandLeaflet = false;
      //       control.state('expand');
      //       this.floorMap.invalidateSize();
      //     }
      //   }]
      // });
      //toggle.addTo(this.floorMap);

      this.generateFloorMapData();
      //this.adjustHeight();
    }, 100);
  }
  startTickListeners() {
    const tickCount = 0;
    if (this.enablePlayback) {
      this.updateIncidentUpdates(Date.now());
    } else {
      this.updateIncidentUpdates(null);
    }
  }
  // Make sure to import d3 if you use modules
  // import * as d3 from 'd3';
  openUserClusterListUi(data: any) {
    let incidentId = this.sessionOptionService.getSessionData()?.event?.id;
    if (!incidentId) {
      if (this.floorMapEntity.building?.incidents?.length > 0) {
        incidentId = this.floorMapEntity.building.incidents[0].eventId;
      }
    }
    if (!incidentId) {
      incidentId = this.floorMapEntity.eventId;
    }

    let clusterSize = 0;
    let lat = 0;
    let lon = 0;
    if (data.dataIsCluster && data.data) {
      clusterSize = data.data.clusterSize ?? 0;
      lat = data.data.position?.coordinates?.[1] ?? 0;
      lon = data.data.position?.coordinates?.[0] ?? 0;
    }

    const statusesStr = data.statuses ? data.statuses.toString() : '';
    const timestampStr = data.timestamp ? data.timestamp.toString() : '';
    const floorPerimeterId = this.floorMapEntity.perimeterId;
    const floorType = 'FLOOR';


    this.closeUserClusterModal('cluster-refresh');

    this.userClusterModalRef = this.modalService.open(UserClusterListUiComponent, {
      backdrop: false,
      keyboard: false,
      windowClass: 'modal-class'
    });
    const modalInputs = {
      dataIsCluster: data.dataIsCluster,
      data: data.data,
      statuses: data.statuses,
      userData: data.userData,
      incidentId,
      entity: this.floorMapEntity,
      lat,
      lon,
      clusterSize,
      timestamp: timestampStr,
      floorPerimeterId,
      floorType
    };
    Object.keys(modalInputs).forEach(key => {
      (this.userClusterModalRef!.componentInstance as any)[key] = (modalInputs as any)[key];
    });

    Object.keys(data).forEach(key => {
      this.userClusterModalRef!.componentInstance[key] = Array.isArray(data[key]) ? [...data[key]] : data[key];
    });

    this.userClusterModalRef.closed.subscribe(() => {
      this.userClusterModalRef = undefined;
    });
    this.userClusterModalRef.dismissed.subscribe(() => {
      this.userClusterModalRef = undefined;
    });
  }
  private closeUserClusterModal(reason: string = 'monitor-mode-toggle'): void {
    if (!this.userClusterModalRef) {
      return;
    }
    this.userClusterModalRef.close(reason);
    this.userClusterModalRef = undefined;
  }
  defineClusterIcon(cluster: any): L.DivIcon {
    const radiusMax = 32;
    const children = cluster.getAllChildMarkers();
    const num = children[0].dataIsCluster ? children[0].total : children.length;
    const strokeWidth = 1;
    let number = num < 1000 ? 4 : 0;
    number = num < 100 ? 8 : number;
    number = num < 10 ? 12 : number;
    const outerR = radiusMax - 2 * strokeWidth - number;
    const innerR = outerR - 10;
    const iconDim = (outerR + strokeWidth) * 2;

    // d3.nest is deprecated, use d3.group in d3 v6+
    // We'll use d3.group and convert to the expected structure for bakeThePie
    const grouped = d3.group(children, (d: any) => {
      if (d.userData?.status.toLowerCase().includes('suspect')) {
        return 'suspect';
      } else {
        return d.status;
      }
    });

    // Convert d3.group result to array of {key, values}
    const data = Array.from(grouped, ([key, values]) => ({ key, values }));

    const html = this.globalSettingsService.bakeThePie({
      data: data,
      valueFunc: (d: any) => d.values.length,
      strokeWidth: strokeWidth,
      outerRadius: outerR,
      innerRadius: innerR,
      pieClass: 'cluster-pie',
      pieLabel: num,
      pieLabelClass: 'marker-cluster-pie-label',
      pathClassFunc: (d: any) => 'arc-category-' + d.data.key,
    });

    const pIcon = new L.DivIcon({
      html: html,
      className: 'marker-cluster',
      iconSize: new L.Point(iconDim, iconDim),
    });

    return pIcon;
  }

  poller() {
    // getSopInfo();
    this.getFloorUserInBBox();
    this.getFloorUserInBBoxAsCluster();
    this.updatePerimeterLayers();
  }

  getFloorUserInBBox() {
    let incidentId: string | undefined;
    const incident: any = {};
    // Determine incidentId ********* TO DO for active incident **************
    if (this.enableLiveMode) {
      incidentId = this.cameraService.getCurrentIncidentId();
    } else if (
      this.floorMapEntity?.building?.incidents &&
      this.floorMapEntity.building.incidents.length > 0
    ) {
      const incident = this.floorMapEntity.building.incidents[0];
      incidentId = incident.eventId;
    }
    else {
      incidentId = this.cameraService.getCurrentIncidentId();
    }
    // } else {
    //   const procallDevices = this.processProcallDevices();
    //   if (procallDevices.length > 0) {
    //     incidentId = procallDevices[0].procallId;
    //   }
    // }

    if (!incidentId) {
      // Calculate bbox from beacons
      let bbox: number[] | undefined;
      const beacons = this.getBeaconsonList();
      const lats = _.map(beacons, (o) => o.position.coordinates[1]);
      const lons = _.map(beacons, (o) => o.position.coordinates[0]);
      if (lats.length > 0) {
        let latMin = _.min(lats);
        let latMax = _.max(lats);
        let lonMin = _.min(lons);
        let lonMax = _.max(lons);
        if (lats.length === 1) {
          latMin -= 0.00001;
          lonMin -= 0.00001;
          latMax += 0.00001;
          lonMax += 0.00001;
        }
        bbox = [lonMin, latMin, lonMax, latMax];
      }
      if (!bbox && this.floorMapEntity?.locations) {
        const lats = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[0] : (o as any).lat));
        const lons = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[1] : (o as any).lng));
        if (lats.length > 0) {
          bbox = [_.min(lons), _.min(lats), _.max(lons), _.max(lats)];
        }
      }
      const floorPerimeterId = this.floorMapEntity.perimeterId;
      const floorType = 'FLOOR';
      const statuses: any[] = [];
      if (this.data.dangerChecked) {
        statuses.push('DANGER');
      }
      if (this.data.safeChecked) {
        statuses.push('SAFE');
      }
      if (this.data.unknownChecked) {
        statuses.push('UNKNOWN');
      }
      if (this.data.suspectChecked) {
        statuses.push('SUSPECT');
      }
      const floorUserRequest: FloorUsersRequest = {
        gridSize: 50,
        threshold: 250,
        bbox: bbox ?? [0, 0, 0, 0],
        floorPerimeterId: floorPerimeterId,
        floorType: floorType,
      };
      this.dataService
        .getUserOnFloorOnMonitorMode(floorUserRequest)
        .subscribe((data: FloorUserResponse) => {
          if (data && data.devices && data.devices) {
            this.dataIsCluster = false;
            this.floorUserDevice = data.devices;
            this.updateLeafFloorUserDeviceMarkers(
              this.floorUserDevice,
              statuses
            );
            this.updateUserListsFromResults(this.floorUserDevice);
            // this.data.users = data.users;
            // this.data.lastPage = data.lastPage;
            // this.data.page = data.page;
          }
        });
    } else if (incidentId) {
      // Calculate bbox from beacons
      let bbox: number[] | undefined;
      const beacons = this.getBeaconsonList();
      const lats = _.map(beacons, (o) => o.position.coordinates[1]);
      const lons = _.map(beacons, (o) => o.position.coordinates[0]);
      if (lats.length > 0) {
        let latMin = _.min(lats);
        let latMax = _.max(lats);
        let lonMin = _.min(lons);
        let lonMax = _.max(lons);
        if (lats.length === 1) {
          latMin -= 0.00001;
          lonMin -= 0.00001;
          latMax += 0.00001;
          lonMax += 0.00001;
        }
        bbox = [lonMin, latMin, lonMax, latMax];
      }
      if (!bbox && this.floorMapEntity?.locations) {
        const lats = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[0] : (o as any).lat));
        const lons = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[1] : (o as any).lng));
        if (lats.length > 0) {
          bbox = [_.min(lons), _.min(lats), _.max(lons), _.max(lats)];
        }
      }
      const floorPerimeterId = this.floorMapEntity.perimeterId;
      const floorType = 'FLOOR';
      const statuses: any[] = [];
      if (this.data.dangerChecked) {
        statuses.push('DANGER');
      }
      if (this.data.safeChecked) {
        statuses.push('SAFE');
      }
      if (this.data.unknownChecked) {
        statuses.push('UNKNOWN');
      }
      if (this.data.suspectChecked) {
        statuses.push('SUSPECT');
      }
      if (statuses.length === 0) {
        statuses.push('DANGER', 'SAFE', 'UNKNOWN');
      }
      const floorUserRequest: FloorUsersRequest = {
        gridSize: 50,
        threshold: 250,
        bbox: bbox ?? [0, 0, 0, 0],
        floorPerimeterId: floorPerimeterId,
        floorType: floorType,
        incidentId: incidentId,
        statuses: statuses,
      };
      this.dataService
        .findOrClusterUsersByBbox(floorUserRequest)
        .subscribe((data: any) => {
          if (data && data.users) {
            this.dataIsCluster = false;
            this.floorUserDevice = data.users;
            this.updateLeafFloorUserDeviceMarkers(
              this.floorUserDevice,
              statuses
            );
            this.updateUserListsFromResults(this.floorUserDevice);
            //this.updateLeafletFloorUserInfoByServerMethod();
          }
        });
    }
  }
  private getBeaconsonList() {
    const beacons: any[] = [];
    if (this.floorMapEntity.perimeters) {
      _.forEach(this.floorMapEntity.perimeters, (perimeter: any) => {
        // Skip floor perimeters - only process room perimeters
        if (perimeter && (perimeter.perimeterType === 'FLOOR' || perimeter.type === 'FLOOR')) {
          return; // Skip this perimeter
        }
        _.forEach(perimeter.beacons, (beacon: any) => {
          // Include perimeter info so we can calculate center
          beacons.push({
            ...beacon,
            perimeter: perimeter
          });
        });
      });
    }
    return beacons;
  }

  /**
   * Calculate the center point (centroid) of a polygon from its locations array
   * Uses the shoelace formula to calculate the true geometric centroid
   * Locations are in [lat, lon] format
   */
  private calculatePolygonCenter(locations: number[][]): { lat: number, lon: number } | null {
    if (!locations || locations.length === 0) {
      return null;
    }

    // Filter out invalid locations
    const validLocations = locations.filter((loc: number[]) =>
      Array.isArray(loc) && loc.length >= 2 &&
      typeof loc[0] === 'number' && typeof loc[1] === 'number' &&
      !isNaN(loc[0]) && !isNaN(loc[1])
    );

    if (validLocations.length === 0) {
      return null;
    }

    // Need at least 3 points to form a polygon
    if (validLocations.length < 3) {
      // Fallback to simple average for less than 3 points
      let sumLat = 0;
      let sumLon = 0;
      validLocations.forEach((loc: number[]) => {
        const [lat, lon] = loc;
        sumLat += lat;
        sumLon += lon;
      });
      return {
        lat: sumLat / validLocations.length,
        lon: sumLon / validLocations.length
      };
    }

    // Use shoelace formula to calculate the centroid
    // For geographic coordinates: x = longitude, y = latitude
    // Locations are in [lat, lon] format, so: y = lat, x = lon
    let signedArea = 0;
    let centroidX = 0; // longitude
    let centroidY = 0; // latitude

    // Ensure the polygon is closed (first point equals last point)
    const closedLocations = [...validLocations];
    const first = closedLocations[0];
    const last = closedLocations[closedLocations.length - 1];
    const isClosed = Math.abs(first[0] - last[0]) < 1e-10 && Math.abs(first[1] - last[1]) < 1e-10;
    if (!isClosed) {
      closedLocations.push([first[0], first[1]]);
    }

    // Apply shoelace formula for centroid calculation
    // For polygon with vertices (xi, yi):
    // Area = 0.5 * Σ(xi * yi+1 - xi+1 * yi)
    // Cx = (1/6A) * Σ(xi + xi+1)(xi * yi+1 - xi+1 * yi)
    // Cy = (1/6A) * Σ(yi + yi+1)(xi * yi+1 - xi+1 * yi)
    // Where: xi = lon, yi = lat
    for (let i = 0; i < closedLocations.length - 1; i++) {
      const [lat1, lon1] = closedLocations[i];
      const [lat2, lon2] = closedLocations[i + 1];

      // Cross product: xi * yi+1 - xi+1 * yi = lon1 * lat2 - lon2 * lat1
      const crossProduct = lon1 * lat2 - lon2 * lat1;
      signedArea += crossProduct;

      // Accumulate centroid components
      // For x (longitude): (xi + xi+1) * crossProduct = (lon1 + lon2) * crossProduct
      // For y (latitude): (yi + yi+1) * crossProduct = (lat1 + lat2) * crossProduct
      centroidX += (lon1 + lon2) * crossProduct;
      centroidY += (lat1 + lat2) * crossProduct;
    }

    // Calculate signed area (divide by 2)
    signedArea = signedArea / 2;

    // Check if area is too small (degenerate polygon)
    if (Math.abs(signedArea) < 1e-10) {
      // Fallback to simple average
      let sumLat = 0;
      let sumLon = 0;
      validLocations.forEach((loc: number[]) => {
        const [lat, lon] = loc;
        sumLat += lat;
        sumLon += lon;
      });
      return {
        lat: sumLat / validLocations.length,
        lon: sumLon / validLocations.length
      };
    }

    // Calculate centroid coordinates
    // Cx = centroidX / (6 * signedArea)
    // Cy = centroidY / (6 * signedArea)
    const areaFactor = 1 / (6 * signedArea);
    return {
      lat: centroidY * areaFactor,
      lon: centroidX * areaFactor
    };
  }

  updateLeafFloorUserDeviceMarkers(data: any, statuses?: any[]): void {
    // Clear existing layers (Leaflet markers)
    if (this.clusterMarkers) {
      this.clusterMarkers.clearLayers();
    }
    if (this.firstResponderMarkers) {
      this.firstResponderMarkers.clearLayers();
    }
    if (this.trackingDeviceMarkers) {
      this.trackingDeviceMarkers.clearLayers();
    }

    // ONLY clear Three.js users when data is empty or floor changed
    // DON'T clear them on every poll if data hasn't changed much
    if (!data || data.length === 0) {
      this.clearUsersInThreeJs();
      // Clear stored data when no data
      this.floorUserDevice = [];
      this.lastUserDataForClustering = null;
      return;
    }

    statuses = statuses ?? [];
    const dataIsCluster = data.dataIsCluster;

    // Set cluster radius based on cluster mode
    if (this.clusterMarkers) {
      this.clusterMarkers.options.maxClusterRadius = dataIsCluster ? 1 : 80;
    }

    if (dataIsCluster) {
      data.rows.forEach((d: any) => {
        const numDanger = d.danger ? parseInt(d.danger, 10) : 0;
        const numSafe = d.safe ? parseInt(d.safe, 10) : 0;
        const numUnknown = d.unknown ? parseInt(d.unknown, 10) : 0;
        const total = numDanger + numSafe + numUnknown;

        const numMax = Math.max(numDanger, numSafe, numUnknown);
        const numMaxScale = numMax > 100 ? 100 : numMax;
        const scaleFactor = numMax ? numMaxScale / numMax : 1;
        const numDangerScale = Math.ceil(numDanger * scaleFactor);
        const numSafeScale = Math.ceil(numSafe * scaleFactor);
        const numUnknownScale = Math.ceil(numUnknown * scaleFactor);

        const lat = d.position.coordinates[1];
        const lon = d.position.coordinates[0];

        // Add danger markers
        for (let i = 0; i < numDangerScale; i++) {
          const marker = this.defineMarker(lat, lon, 'unsafe', this.unsafeIcon);
          (marker as any).marker.dataIsCluster = dataIsCluster;
          (marker as any).marker.total = total;
          (marker as any).marker.data = d;
          (marker as any).marker.statuses = statuses;

          this.clusterMarkers.addLayer(marker);
        }
        // Add safe markers
        for (let i = 0; i < numSafeScale; i++) {
          const marker = this.defineMarker(lat, lon, 'safe', this.safeIcon);
          (marker as any).marker.dataIsCluster = dataIsCluster;
          (marker as any).marker.total = total;
          (marker as any).marker.data = d;
          (marker as any).marker.statuses = statuses;
          this.clusterMarkers.addLayer(marker);
        }
        // Add unknown markers
        for (let i = 0; i < numUnknownScale; i++) {
          const marker = this.defineMarker(
            lat,
            lon,
            'unknown',
            this.unknownIcon
          );
          (marker as any).marker.dataIsCluster = dataIsCluster;
          (marker as any).marker.total = total;
          (marker as any).marker.data = d;
          (marker as any).marker.statuses = statuses;
          this.clusterMarkers.addLayer(marker);
        }
      });
    } else {
      data.forEach((d: any) => {
        const lat = d.position.coordinates[1];
        const lon = d.position.coordinates[0];
        let status = d.status?.toLowerCase() || 'unknown';
        if (status === 'danger') {
          status = 'unsafe';
        }
        let icon = this.unknownIcon;
        if (status === 'unsafe') {
          icon = this.unsafeIcon;
        } else if (status === 'safe') {
          icon = this.safeIcon;
        }
        this.marker = {};
        this.marker = this.defineMarker(lat, lon, status, icon);
        this.marker.dataIsCluster = this.dataIsCluster;
        this.marker.userData = d;
        this.marker.statuses = statuses;
        this.marker.bindPopup(d.name);

        // Add click handler to emit userClicked event
        this.marker.on('click', () => {
          this.userClicked.emit({
            userId: d.userId || d.deviceId,
            roomId: d.roomId || '',
            mesh: null
          });
        });

        // Add to clusterMarkers but hide from map - only show Three.js badges
        this.clusterMarkers.addLayer(this.marker);
      });
    }

    // Check if data has changed significantly before refreshing badges
    const shouldRefreshBadges = this.shouldRefreshUserBadges(data, statuses);

    if (shouldRefreshBadges) {
      // Store the current data for comparison next time
      this.lastUserDataForClustering = { data, statuses };

      // Render users in Three.js after updating Leaflet markers
      if (this.threeScene && this.threeJsInitialized) {
        setTimeout(() => {
          this.renderUsersInThreeJs(data, statuses);
        }, 100);
      }
    }
  }

  /**
   * Check if user badge data has changed enough to warrant a refresh
   * Returns true only when data has changed significantly
   */
  private shouldRefreshUserBadges(newData: any, newStatuses: any[]): boolean {
    // If no previous data, always refresh
    if (!this.lastUserDataForClustering) {
      return true;
    }

    const oldData = this.lastUserDataForClustering.data;
    const oldStatuses = this.lastUserDataForClustering.statuses;

    // If statuses changed (user checked/unchecked danger/safe/unknown), refresh
    if (JSON.stringify(newStatuses) !== JSON.stringify(oldStatuses)) {
      return true;
    }

    // Check if data structure is the same
    const newIsCluster = (newData as any).dataIsCluster;
    const oldIsCluster = (oldData as any).dataIsCluster;

    if (newIsCluster !== oldIsCluster) {
      return true; // Data type changed (cluster ↔ individual)
    }

    if (newIsCluster && oldIsCluster) {
      // Both are clustered data - check cluster counts
      const newRows = (newData as any).rows || [];
      const oldRows = (oldData as any).rows || [];

      if (newRows.length !== oldRows.length) {
        return true; // Number of clusters changed
      }

      // Check if any cluster's user count changed significantly
      for (let i = 0; i < newRows.length; i++) {
        const newCluster = newRows[i];
        const oldCluster = oldRows[i];

        if (!newCluster || !oldCluster) continue;

        const newTotal = (newCluster.safe ? parseInt(newCluster.safe) : 0) +
          (newCluster.danger ? parseInt(newCluster.danger) : 0) +
          (newCluster.unknown ? parseInt(newCluster.unknown) : 0);

        const oldTotal = (oldCluster.safe ? parseInt(oldCluster.safe) : 0) +
          (oldCluster.danger ? parseInt(oldCluster.danger) : 0) +
          (oldCluster.unknown ? parseInt(oldCluster.unknown) : 0);

        // If user count changed, refresh
        if (newTotal !== oldTotal) {
          return true;
        }

        // Check if cluster position changed
        if (
          oldCluster.position?.coordinates?.[0] !== newCluster.position?.coordinates?.[0] ||
          oldCluster.position?.coordinates?.[1] !== newCluster.position?.coordinates?.[1]
        ) {
          return true;
        }
      }
    } else if (Array.isArray(newData) && Array.isArray(oldData)) {
      // Both are individual user data
      if (newData.length !== oldData.length) {
        return true; // Number of users changed
      }

      // Check if any user's status changed
      const newUsers = new Map(newData.map((d: any) => [d.deviceId || d.userId, d]));
      const oldUsers = new Map(oldData.map((d: any) => [d.deviceId || d.userId, d]));

      for (const [id, oldUser] of oldUsers) {
        const newUser = newUsers.get(id);
        if (!newUser) {
          return true; // User disappeared
        }

        if (oldUser.status !== newUser.status) {
          return true; // User status changed
        }
        if (
          oldUser.position?.coordinates?.[0] !== newUser.position?.coordinates?.[0] ||
          oldUser.position?.coordinates?.[1] !== newUser.position?.coordinates?.[1]
        ) {
          return true;
        }
      }
    }

    // Data hasn't changed significantly
    return false;
  }

  defineMarker(
    lat: number,
    lon: number,
    status: string,
    icon: L.Icon
  ): L.Marker {
    const marker = L.marker([lat, lon], {
      icon,
      zIndexOffset: 1000         // Force them above everything else in the pane
    });
    // Attach custom properties (TypeScript will warn, so use type assertion)
    (marker as any).status = status;
    return marker;
  }

  ngOnDestroy() {
    this.floorService.toggleGrandchild(false);
    this.destroy$.next();
    this.destroy$.complete();
    this.removeUserClusterIn3D();
    if (this.pollerSubscription) {
      this.pollerSubscription.unsubscribe();
    }
    if (this.handlerLeftClick) {
      this.handlerLeftClick.destroy();
      this.handlerLeftClick = undefined;
    }
    // Cleanup Cesium GLB model
    this.blenderFloorMap.cleanupGlbModel(this.viewer);
    // Cleanup Three.js rendering
    this.cleanupThreeJs();
    // Cleanup device markers
    if (this.deviceClusterGroup && this.floorMap) {
      this.floorMap.removeLayer(this.deviceClusterGroup);
      this.deviceClusterGroup.clearLayers();
      this.deviceClusterGroup = null;
    }
    this.deviceMarkers.clear();
  }

  addExpandCollapseButton() {
    if (!this.floorMap) return;

    // @ts-ignore
    L.easyButton({
      states: [
        {
          stateName: 'expand',
          icon: 'fa-up-right-and-down-left-from-center',
          title: 'Expand',
          onClick: (btn: any) => {
            this.toggleCollapse(false);
            btn.state('collapse');
          },
        },
        {
          stateName: 'collapse',
          icon: 'fa-down-left-and-up-right-to-center',  // FontAwesome icon
          title: 'Compress',
          onClick: (btn: any) => {
            this.toggleCollapse(true);
            btn.state('expand');
          },
        },
      ],
    }).addTo(this.floorMap);
  }

  toggleCollapse(collapse: boolean) {
    this.isCollapsed = collapse;
    if (!this.floorMap) return; // exit if floorMap is undefined

    const mapContainer = this.floorMap.getContainer();
    if (collapse) {
      mapContainer.classList.add('map-collapsed');
      mapContainer.classList.remove('map-expanded');
    } else {
      mapContainer.classList.add('map-expanded');
      mapContainer.classList.remove('map-collapsed');
    }

    setTimeout(() => {
      this.floorMap!.invalidateSize(); // safe now
      // Resize Three.js renderer when map container size changes
      this.onWindowResize();
    }, 300);

    // Emit to parent
    this.collapseChange.emit(this.isCollapsed);
  }

  generateFloorMapData() {
    if (this.floorMap) {
      this.floorMap.remove();
      this.floorMap = undefined;
    }
    const mapEntity = this.floorMapEntity;
    //let testData = this.dataContextService.getMapEntity(mapEntity.id);
    const mapOptions =
      ConfigurationManagerService.configSettings.floorAttributeVisualizer
        .leaflet.map.options;
    const centerLat = mapEntity?.centerpoint?.latitude; // Replace with your value
    const centerLng = mapEntity?.centerpoint?.longitude; // Replace with your value
    this.floorMap = L.map(
      this.floorMapElement.nativeElement,
      mapOptions
    ).setView([centerLat, centerLng], 18);

    // Ensure tooltips and popups appear above Three.js canvas
    if (this.floorMap) {
      const tooltipPane = this.floorMap.getPane('tooltipPane');
      if (tooltipPane) {
        (tooltipPane as HTMLElement).style.zIndex = '2000';
      }
      const popupPane = this.floorMap.getPane('popupPane');
      if (popupPane) {
        (popupPane as HTMLElement).style.zIndex = '2000';
      }
    }

    this.generateLeafletmap();
    this.addExpandCollapseButton();
    this.toggleCollapse(true);

    // Intercept Leaflet zoom button clicks to control Three.js zoom
    this.interceptLeafletZoomButtons();

    // Initialize Three.js rendering for the floor after Leaflet is ready
    setTimeout(() => {
      this.threeJsInitialized = false;
      this.initializeThreeJsFloorMap();
    }, 100);
  }
  showPerimeterDetails() {
    if (this.floorMapEntity.perimeters) {
      // Filter out floor perimeters - only emit room perimeters (same as Perimeters tab expects)
      const allPerimeters = typeof this.floorMapEntity.perimeters === 'object'
        ? Object.values(this.floorMapEntity.perimeters)
        : Array.isArray(this.floorMapEntity.perimeters)
          ? this.floorMapEntity.perimeters
          : [];
      // Filter out floor perimeters (perimeterType === 'FLOOR' or type === 'FLOOR')
      const roomPerimeters = allPerimeters.filter((p: any) => {
        return p && p.perimeterType !== 'FLOOR' && p.type !== 'FLOOR';
      });
      // Convert back to object format if needed, or emit as array
      const filteredPerimeters: any = {};
      roomPerimeters.forEach((p: any) => {
        if (p && (p.id || p.arcAngelId || p.perimeterId)) {
          const key = String(p.id || p.arcAngelId || p.perimeterId);
          filteredPerimeters[key] = p;
        }
      });
      this.perimeterChange.emit(filteredPerimeters);
    }
  }

  showNumberOfBeacons() {
    // Clear existing beacon layers from map first
    this.hideBeaconLayerOnMap();
    this.beaconLayers = [];

    const beaconsList = this.getBeaconsonList();
    this.numberOfBeaconsChange.emit(beaconsList.length);
    // Create beacon markers with the custom pane
    _.forEach(beaconsList, (beacon: any) => {
      const marker = L.marker(
        [beacon.position.coordinates[1], beacon.position.coordinates[0]],
        {
          icon: this.bluetoothIcon,
          pane: 'bluetooth-pane'
        }
      );
      (marker as any).sensorId = beacon.id;
      marker.bindTooltip(beacon.label);
      this.beaconLayers.push(marker);
    });
  }
  showBeaconLayerOnMap() {
    if (!this.floorMap) return;
    // Add beacons to map
    _.forEach(this.beaconLayers, (layer) => {
      layer.addTo(this.floorMap!);
    });
  }

  hideBeaconLayerOnMap() {
    if (!this.floorMap) return;
    // Remove beacons from map
    _.forEach(this.beaconLayers, (layer) => {
      this.floorMap!.removeLayer(layer);
    });
  }
  generateLeafletmap() {
    // Keep base tile layer visible as background
    const rawTileOptions = ConfigurationManagerService.configSettings?.floorAttributeVisualizer?.leaflet?.tileLayer?.options || {};
    const tileOptions = {
      ...rawTileOptions,
      maxNativeZoom: 19,
    };
    L.tileLayer(
      ConfigurationManagerService.configSettings.floorAttributeVisualizer
        .leaflet.tileLayer.url,
      tileOptions
    )
      .setOpacity(FLOOR_LEAFLET_TILE_OPACITY)
      .addTo(this.floorMap!);
    // Hide Leaflet cluster markers - comment out addLayer to prevent them from showing
    // Keep clusterMarkers for data structure but don't add to map
    // this.floorMap?.addLayer(this.clusterMarkers);

    // Create a custom high z-index pane for floor plan overlay to ensure it is above Three.js canvas (650)
    if (!this.floorMap?.getPane('floorplan-pane')) {
      this.floorMap?.createPane('floorplan-pane');
      const floorplanPane = this.floorMap?.getPane('floorplan-pane');
      if (floorplanPane) {
        floorplanPane.style.zIndex = '660'; // Above Three.js canvas (650)
      }
    }

    // Create a custom high z-index pane for user cluster markers to ensure they are above all floor elements
    this.floorMap?.createPane('user-cluster-pane');
    const userClusterPane = this.floorMap?.getPane('user-cluster-pane');
    if (userClusterPane) {
      userClusterPane.style.zIndex = '700'; // Above Three.js canvas (650)
    }

    // Create a custom high z-index pane for bluetooth markers to ensure they are above all floor elements
    this.floorMap?.createPane('bluetooth-pane');
    const bluetoothPane = this.floorMap?.getPane('bluetooth-pane');
    if (bluetoothPane) {
      bluetoothPane.style.zIndex = '700'; // Above Three.js canvas (650)
    }

    // Create a custom high z-index pane for device markers
    this.floorMap?.createPane('device-pane');
    const devicePane = this.floorMap?.getPane('device-pane');
    if (devicePane) {
      devicePane.style.zIndex = '710'; // Above Three.js canvas (650) and other layers
    }

    this.floorMap?.addLayer(this.firstResponderMarkers);
    this.floorMap?.addLayer(this.trackingDeviceMarkers);

    this.initDeviceClusterGroup();

    this.updateFloorMapView();
  }

  updateFloorMapView(): void {
    if (!this.floorMap || this.isLeafletMoving) return;

    const entity = this.floorMapEntity;
    if (!entity) return;

    // Special building
    if (entity.building?.buildingid === 'w116660354') {
      this.floorMap.setView([36.0875, -115.176681], 17, { animate: false });
    } else {
      // Normal case - fit bounds to floor locations
      const locations = entity?.locations || (entity?.geometry?.coordinates?.[0]?.map((c: any) => [c[1], c[0]]));
      if (Array.isArray(locations) && locations.length >= 2) {
        this.floorMap.fitBounds(locations, { animate: false });
      }
    }

    // Get the number of beacons
    this.showNumberOfBeacons();
    // Show the perimeter details
    this.showPerimeterDetails();
    // Load floor plan image overlay (or clean up if none)
    this.getfloorPlanLayer();
    // Load cameras on the map
    this.loadCamerasOnMap();

    // Call beaconOnChange to apply initial beacon visibility settings
    this.beaconOnChange();

    // Render device location markers on floor map
    this.deviceMarkersOnChange();

    if (this.threeJsInitialized) {
      this.syncThreeJsWithLeaflet();
      this.renderFloorInThreeJs();
    }
  }
  updateperimeterLayer() {
    // Disable Leaflet floor perimeter rendering - Three.js handles floor rendering now
    // Remove existing perimeter layers to prevent double outline rendering
    if (this.floorMapEntity?.perimeters) {
      _.forEach(this.floorMapEntity.perimeters, (perimeter) => {
        if (this.perimeterLayers[perimeter.id]) {
          this.floorMap?.removeLayer(this.perimeterLayers[perimeter.id]);
          delete this.perimeterLayers[perimeter.id];
        }
      });

      // Hard code fire marker on Fifth floor of hp
      const floorLabel = this.floorMapEntity.label;
      const buildingName = this.floorMapEntity.building?.name;
      const incidents = this.floorMapEntity.building?.incidents || [];

      if (
        floorLabel === 'Floor 5' &&
        buildingName &&
        (buildingName === 'Building D' || /aruba/i.test(buildingName))
      ) {
        // *************************  TODO : For Incident
        if (incidents.length > 0 && this.isIncidentReallyActiveForHP(incidents)) {
          if (!this.fireMarkerLayer) {
            const incidentName = incidents[0].name;
            let iconToUse = this.fireIcon;
            if (incidentName === 'Fire Emergency') iconToUse = this.fireIcon;
            if (incidentName === 'Medical Emergency') iconToUse = this.emsIcon;
            if (incidentName === 'Police Emergency') iconToUse = this.policeIcon;
            this.fireMarkerLayer = L.marker([37.382115, -121.981639], {
              icon: iconToUse,
              title: incidentName
            }).addTo(this.floorMap!);
          }
        } else if (this.fireMarkerLayer) {
          this.floorMap?.removeLayer(this.fireMarkerLayer);
          this.fireMarkerLayer = undefined;
        }
      }
    }
  }

  updatePerimeterLayers() {
    // Three.js renders floor/rooms — remove any legacy Leaflet perimeter strokes.
    if (!this.floorMapEntity?.perimeters) {
      return;
    }

    _.forEach(this.floorMapEntity.perimeters, (perimeter) => {
      if (this.perimeterLayers[perimeter.id]) {
        this.floorMap?.removeLayer(this.perimeterLayers[perimeter.id]);
        delete this.perimeterLayers[perimeter.id];
      }
      // Add perimeter layers on top of floorplan
      // if (this.floorMap) {
      //   this.perimeterLayers[perimeter.id] = this.addPolygonToMap(
      //     perimeter,
      //     perimeter.name,
      //     this.floorMap
      //   );
      // }
    });

    const floorLabel = this.floorMapEntity.label;
    const buildingName = this.floorMapEntity.building?.name;
    const incidents = this.floorMapEntity.building?.incidents || [];

    if (
      floorLabel === 'Floor 5' &&
      buildingName &&
      (buildingName === 'Building D' || buildingName.match(/aruba/i))
    ) {
      if (incidents.length > 0 && this.isIncidentReallyActiveForHP(incidents)) {
        if (!this.fireMarkerLayer) {
          const incidentName = incidents[0].name;
          let iconToUse = this.fireIcon;
          if (incidentName === 'Fire Emergency') {
            iconToUse = this.fireIcon;
          }
          if (incidentName === 'Medical Emergency') {
            iconToUse = this.emsIcon;
          }
          if (incidentName === 'Police Emergency') {
            iconToUse = this.policeIcon;
          }

          this.fireMarkerLayer = L.marker([37.382115, -121.981639], {
            icon: iconToUse,
            title: incidentName,
          }).addTo(this.floorMap!);
        }
      } else if (this.fireMarkerLayer) {
        this.floorMap?.removeLayer(this.fireMarkerLayer);
        this.fireMarkerLayer = undefined;
      }
    }
  }

  // Helper method to check if incident is really active for HP
  private isIncidentReallyActiveForHP(incidents: any[]): boolean {
    // This is a placeholder implementation
    // You may need to implement the actual logic based on your requirements
    return incidents.some(
      (incident) =>
        incident.status === 'active' || incident.status === 'pending'
    );
  }

  // add polygon on map
  addPolygonToMap(
    polygonData: any,
    polygonName: string,
    map: L.Map,
    date?: any
  ): L.Polygon {
    // Helper to convert RGB to hex
    function rgbToHex(r: number, g: number, b: number): string {
      return (
        '#' +
        [r, g, b]
          .map((x) => {
            const hex = Math.round(x).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
          })
          .join('')
      );
    }

    let outlineColor: string | undefined;
    let strokeWeight: number | undefined;

    if (polygonData.outlineColor) {
      outlineColor = rgbToHex(
        polygonData.outlineColor.red * 255,
        polygonData.outlineColor.green * 255,
        polygonData.outlineColor.blue * 255
      );
    }

    if (polygonData.outlineWidth) {
      strokeWeight = polygonData.outlineWidth;
    }

    const fillColor = rgbToHex(
      polygonData.material.red * 255,
      polygonData.material.green * 255,
      polygonData.material.blue * 255
    );
    let fillOpacity = polygonData.material.alpha;

    if (polygonData.layerPath === 'ArcAngel//Perimeters') {
      fillOpacity = 0.5;
    }

    // Ensure locations is an array of [lat, lng] pairs
    const locations = polygonData.locations;

    const leafletPolygon = L.polygon(locations, {
      color: outlineColor,
      weight: strokeWeight,
      fillColor: fillColor,
      opacity: fillOpacity,
    });

    const displayName = polygonName || polygonData.name || polygonData.label || `Room ${polygonData.id || ''}`;

    leafletPolygon.bindTooltip(displayName, {
      direction: 'center',
      permanent: false,
      className: 'room-label-tooltip',
      opacity: 1
    });

    leafletPolygon.addTo(map).bindPopup(displayName);
    leafletPolygon.bringToFront();
    (leafletPolygon as any).arcAngelUpdatedDate = date;

    return leafletPolygon;
  }
  updateUserListsFromResults(results: any): void {
    if (results) {

      const users = results.map((device: any) => ({
        deviceId: device.deviceId?.toString(),
        name: device.name,
        src: device?.status == 'UNKNOWN' ? 'assets/img/arcangel/user_unknown.svg' : device?.status == 'DANGER' ? 'assets/img/arcangel/user_in_danger.svg' : device?.status == 'SAFE' ? 'assets/img/arcangel/user_safe.svg' : 'assets/img/arcangel/user_unknown.svg',
        status: device?.status || 'UNKNOWN',
        userId: device.userId?.toString(),
        visible: false,
      }));
      this.data.users = users;
      this.userOnFloorsChange.emit(users);
      this.floorUserDevicesRaw.emit(results);
    }
  }
  // load cameras on map
  loadCamerasOnMap() {
    this.dataService.getSensorList().subscribe((results: any) => {
      if (results) {
        const buildingPerimeterId = this.floorMapEntity?.building?.perimeterId;
        const floorPerimeterId = this.floorMapEntity?.perimeterId;

        // Clear existing camera layers from map
        if (this.floorMap) {
          _.forEach(this.cameraLayer, (layer) => {
            this.floorMap!.removeLayer(layer);
          });
        }
        this.cameraLayer = [];

        const cameraListForFloor = this.getSensorListForFloor(
          buildingPerimeterId,
          floorPerimeterId,
          results.buildings,
          results.cameraList
        );
        if (cameraListForFloor && cameraListForFloor.length > 0) {
          this.cameraLayerChange.emit(cameraListForFloor);
          _.forEach(cameraListForFloor, (camera: any) => {
            if (camera?.position?.coordinates && Array.isArray(camera.position.coordinates) && camera.position.coordinates.length >= 2) {
              const lat = camera.position.coordinates[1];
              const lon = camera.position.coordinates[0];
              const cameraMarker = L.marker([lat, lon], {
                icon: this.cameraIcon,
              });
              (cameraMarker as any).sensorId = camera.id;
              (cameraMarker as any).cameraId = camera.id;
              (cameraMarker as any).cameraName = camera.label || camera.name;
              cameraMarker.bindTooltip(camera.label || camera.name);
              cameraMarker.on('click', () => {
                if ((cameraMarker as any).sensorId) {
                  this.viewCamera(camera);
                }
              });
              this.cameraLayer.push(cameraMarker);
              if (this.showCameraLayer && this.floorMap) {
                cameraMarker.addTo(this.floorMap);
              }
            }
          });
          this.numberOfCameras = this.cameraLayer.length;
          this.numberOfCamerasChange.emit(this.numberOfCameras);
        } else {
          this.numberOfCameras = 0;
          this.numberOfCamerasChange.emit(0);
        }

        // Clear existing access control layers from map
        if (this.floorMap) {
          _.forEach(this.accessControlLayers, (layer) => {
            this.floorMap!.removeLayer(layer);
          });
        }
        this.accessControlLayers = [];

        const accessControlListForFloor = this.getSensorListForFloor(
          buildingPerimeterId,
          floorPerimeterId,
          results.buildings,
          results.accessControlList
        );
        this.accessControlListForFloor = accessControlListForFloor;
        this.accessControlLayerChange.emit(accessControlListForFloor);

        if (accessControlListForFloor && accessControlListForFloor.length > 0) {
          _.forEach(accessControlListForFloor, (sensor: any) => {
            if (sensor?.position?.coordinates && Array.isArray(sensor.position.coordinates) && sensor.position.coordinates.length >= 2) {
              const lat = sensor.position.coordinates[1];
              const lon = sensor.position.coordinates[0];
              const sensorMarker = L.marker([lat, lon], {
                icon: this.accessControlIcon,
              });
              (sensorMarker as any).sensorId = sensor.id;
              sensorMarker.bindTooltip(sensor.label || sensor.name);
              this.accessControlLayers.push(sensorMarker);
              if (this.showAccessControlLayer && this.floorMap) {
                sensorMarker.addTo(this.floorMap);
              }
            }
          });
          this.numberOfAccessControls = this.accessControlLayers.length;
          this.numberOfAccessControlsChange.emit(this.numberOfAccessControls);
        } else {
          this.numberOfAccessControls = 0;
          this.numberOfAccessControlsChange.emit(0);
        }

        // Clear existing raspberry pi layers from map
        if (this.floorMap) {
          _.forEach(this.raspberryPiLayers, (layer) => {
            this.floorMap!.removeLayer(layer);
          });
        }
        this.raspberryPiLayers = [];

        const bldg = this.floorMapEntity?.building ?? (typeof this.floorMapEntity?.getParentBuilding === 'function' ? this.floorMapEntity.getParentBuilding() : null);
        const resolvedBuildingPerimeterId = this.floorMapEntity?.building?.perimeterId || bldg?.perimeterId || bldg?.id || buildingPerimeterId;
        const resolvedFloorPerimeterId = this.floorMapEntity?.perimeterId || this.floorMapEntity?.arcAngelId || this.floorMapEntity?.id || floorPerimeterId;

        let raspberryPiListForFloor = this.getSensorListForFloor(
          resolvedBuildingPerimeterId,
          resolvedFloorPerimeterId,
          results.buildings,
          results.raspberryPiList || []
        );

        if ((!raspberryPiListForFloor || raspberryPiListForFloor.length === 0) && results.raspberryPiList && results.raspberryPiList.length > 0) {
          if (resolvedFloorPerimeterId) {
            const matched = results.raspberryPiList.filter((s: any) => String(s.perimeterId) === String(resolvedFloorPerimeterId));
            if (matched.length > 0) {
              raspberryPiListForFloor = matched;
            }
          }
          if (!raspberryPiListForFloor || raspberryPiListForFloor.length === 0) {
            raspberryPiListForFloor = results.raspberryPiList;
          }
        }
        this.raspberryPiListForFloor = raspberryPiListForFloor;
        this.raspberryPiLayerChange.emit(raspberryPiListForFloor);

        if (raspberryPiListForFloor && raspberryPiListForFloor.length > 0) {
          const rpiSensorIds = raspberryPiListForFloor.map((s: any) => s.id);
          this.dataService.findRaspberryPi({ where: { sensorId: rpiSensorIds } }).subscribe({
            next: (rpiResult: any) => {
              const rpiRows = rpiResult?.rows || [];
              _.forEach(raspberryPiListForFloor, (sensor: any) => {
                const foundRpi = rpiRows.find((r: any) => r.sensorId?.toString() === sensor.id?.toString());
                if (foundRpi && foundRpi.id) {
                  sensor.raspberryPiId = foundRpi.id;
                  if (foundRpi.uuid) {
                    sensor.uuid = sensor.uuid || foundRpi.uuid;
                  }
                  const matchingMarker = this.raspberryPiLayers.find((m: any) => String(m.sensorId) === String(sensor.id));
                  if (matchingMarker) {
                    (matchingMarker as any).raspberryPiId = foundRpi.id;
                    if (foundRpi.uuid) {
                      (matchingMarker as any).sensorUuid = foundRpi.uuid;
                    }
                  }
                  this.dataService.getRaspberryPi(foundRpi.id).subscribe({
                    next: (rpiData: any) => {
                      sensor.raspberryPi = rpiData;
                      if (rpiData?.uuid) {
                        sensor.uuid = sensor.uuid || rpiData.uuid;
                        if (matchingMarker) {
                          (matchingMarker as any).sensorUuid = rpiData.uuid;
                        }
                      }
                      this.updateRaspberryPiMarkers();
                      if (this.showRaspberryPiLayer && rpiData?.url) {
                        this.callRaspberryPiUrl(rpiData.url, rpiData.protocol);
                      }
                    },
                    error: (err: any) => {
                      console.error('Error fetching raspberry pi data for id:', foundRpi.id, err);
                    }
                  });
                }
              });
            },
            error: (err: any) => {
              console.error('Error fetching raspberry pi list via findAndCountAll:', err);
            }
          });

          _.forEach(raspberryPiListForFloor, (sensor: any) => {
            let lat: number | null = null;
            let lon: number | null = null;
            if (sensor?.position?.coordinates && Array.isArray(sensor.position.coordinates) && sensor.position.coordinates.length >= 2) {
              lat = Number(sensor.position.coordinates[1]);
              lon = Number(sensor.position.coordinates[0]);
            } else if (sensor?.latitude != null && sensor?.longitude != null) {
              lat = Number(sensor.latitude);
              lon = Number(sensor.longitude);
            } else if (sensor?.lat != null && sensor?.lng != null) {
              lat = Number(sensor.lat);
              lon = Number(sensor.lng);
            }

            if (lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon)) {
              const markerInfo = {
                sensor,
                sensorId: sensor.id,
                sensorUuid: sensor.uuid || sensor.raspberryPi?.uuid || null,
                raspberryPiId: sensor.raspberryPiId,
                perimeterId: sensor.perimeterId
              };
              const count = this.getMarkerDeviceCount(markerInfo);
              const label = sensor.label || sensor.name || 'Raspberry Pi';
              const sensorMarker = L.marker([lat, lon], {
                icon: this.createRaspberryPiDivIcon(count, label),
              });
              (sensorMarker as any).sensor = sensor;
              (sensorMarker as any).sensorId = sensor.id;
              (sensorMarker as any).sensorUuid = sensor.uuid || sensor.raspberryPi?.uuid || null;
              (sensorMarker as any).raspberryPiId = sensor.raspberryPiId;
              (sensorMarker as any).perimeterId = sensor.perimeterId;
              (sensorMarker as any).sensorType = sensor.type;
              (sensorMarker as any).sensorLabel = label;
              (sensorMarker as any).deviceCount = count;
              sensorMarker.bindTooltip(label);
              sensorMarker.on('click', () => {
                this.raspberryPiMarkerClick.emit(sensor);
                if (sensor?.raspberryPiId) {
                  this.dataService.getRaspberryPi(sensor.raspberryPiId).subscribe({
                    next: (rpiData: any) => {
                      sensor.raspberryPi = rpiData;
                      if (rpiData?.url) {
                        this.callRaspberryPiUrl(rpiData.url, rpiData.protocol);
                      }
                    }
                  });
                } else if (sensor?.id) {
                  this.dataService.findRaspberryPi({ where: { sensorId: sensor.id } }).subscribe({
                    next: (rpiResult: any) => {
                      const foundRpi = rpiResult?.rows?.find((r: any) => r.sensorId?.toString() === sensor.id?.toString());
                      if (foundRpi && foundRpi.id) {
                        sensor.raspberryPiId = foundRpi.id;
                        this.dataService.getRaspberryPi(foundRpi.id).subscribe({
                          next: (rpiData: any) => {
                            sensor.raspberryPi = rpiData;
                            if (rpiData?.url) {
                              this.callRaspberryPiUrl(rpiData.url, rpiData.protocol);
                            }
                          }
                        });
                      }
                    }
                  });
                }
              });
              this.raspberryPiLayers.push(sensorMarker);
              if (this.showRaspberryPiLayer && this.floorMap) {
                sensorMarker.addTo(this.floorMap);
              }
            }
          });
          this.numberOfRaspberryPis = this.raspberryPiLayers.length;
          this.numberOfRaspberryPisChange.emit(this.numberOfRaspberryPis);
        } else {
          this.numberOfRaspberryPis = 0;
          this.numberOfRaspberryPisChange.emit(0);
        }
      }
    });
  }
  //// get camera list for floor
  getSensorListForFloor(
    buildingPerimeterId: number | string,
    floorPerimeterId: number | string,
    buildings: any[],
    sensorList: any[]
  ): any[] {
    // Find the correct building
    const building = buildings.find(
      (o) =>
        o.id &&
        buildingPerimeterId &&
        parseInt(o.id) === parseInt(buildingPerimeterId as string)
    );
    if (!building) {
      return [];
    }

    // Find the correct floor
    const floor = (building.floors || []).find(
      (o: any) => parseInt(o.id) === parseInt(floorPerimeterId as string)
    );
    if (!floor) {
      return [];
    }

    // Filter sensors for this floor (descendants or direct match)
    return sensorList.filter(
      (o: any) =>
        (floor.descendants && floor.descendants.includes(o.perimeterId)) ||
        o.perimeterId === floor.id
    );
  }
  getIncidentUpdates(): any[] {
    const updates: any[] = [];
    const perimeterIds = Object.keys(this.floorMapEntity.perimeters);
    if (
      this.floorMapEntity?.building?.incidents[0] &&
      this.floorMapEntity?.building?.incidents[0] &&
      this.floorMapEntity?.building?.incidents.length > 0
    ) {
      this.floorMapEntity.building.incidents.forEach((incident: any) => {
        (incident.updates || []).forEach((update: any) => {
          if (perimeterIds.includes(update.data.perimeterId)) {
            updates.push(update);
          }
        });
      });
    }
    return updates;
  }
  updateIncidentUpdates(currentTime: any): void {
    const dateNow = currentTime ? Cesium.JulianDate.toDate(currentTime) : null;

    // collect updates (skip rejected)
    const updates = _.filter(this.getIncidentUpdates() || [], (o: any) => {
      return o?.data?.status !== 'REJECTED';
    });

    // remove all update icons from map
    _.forEach(this.incidentUpdateMarkers, (v: any, k: any) => {
      try {
        if (this.floorMap && v) {
          this.floorMap.removeLayer(v);
        }
      } catch (e) {
        // ignore removal errors
      }
      delete this.incidentUpdateMarkers[k];
    });

    const updatesToShow: any[] = [];

    _.forEach(updates, (u: any) => {
      let showUpdateIn2D = true;
      if (dateNow && u.validAt) {
        const validFrom = new Date(u.validAt[0]).getTime();
        const validTo = new Date(u.validAt[1]).getTime();
        showUpdateIn2D = dateNow.getTime() >= validFrom && dateNow.getTime() <= validTo;
      }

      if (showUpdateIn2D) {
        const updateIcon = L.icon({ iconUrl: u.icon, iconSize: [32, 32] });

        if (this.incidentUpdateMarkers[u?.data?.id]) {
          // update existing marker
          const marker = this.incidentUpdateMarkers[u.data.id];
          marker.setIcon(updateIcon);
          marker.setLatLng([u.centerpoint.latitude, u.centerpoint.longitude]);
          marker.bindPopup(u.name);
          const date = new Date(u.lastUpdate);
          u.localTime = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
        } else {
          // create new marker
          const updateMarker = L.marker([u.latitude, u.longitude], {
            title: u.name,
            icon: updateIcon,
          });

          updateMarker.bindPopup(u.name);
          updateMarker.addTo(this.floorMap!);

          // attach original update object for handlers that expect it
          (updateMarker as any).updateObj = u;

          // attach click handler if present on the component
          if (typeof (this as any).incidentUpdateMarkerOnClick === 'function') {
            updateMarker.on('click', (e: any) => {
              try {
                (this as any).incidentUpdateMarkerOnClick(e);
              } catch (err) {
                // swallow handler errors
                // console.error('incidentUpdateMarkerOnClick error', err);
              }
            });
          }

          this.incidentUpdateMarkers[u.data.id] = updateMarker;
        }

        updatesToShow.push(u);
      }
    });

    this.updates = updatesToShow;
    this.updateChanges.emit(updatesToShow);
  }

  /**
   * Fetches the floorplan binary data and adds it as a rotated image overlay to the Leaflet map.
   */
  getfloorPlanLayer() {
    // Remove any existing overlay before loading a new one or if no floorplan exists
    if (this.floorPlanOverlay && this.floorMap) {
      this.floorMap.removeLayer(this.floorPlanOverlay);
      this.floorPlanOverlay = null;
    }

    if (!this.floorMapEntity?.floorplanId || !this.floorMapEntity?.rotationCoords) {
      return;
    }

    this.arcAngelPapiService.getFloorplanFile(this.floorMapEntity.floorplanId).then((response: any) => {
      if (response && this.floorMap) {
        const flipCoords = (c: any) => [c[1], c[0]];
        const point1 = flipCoords(this.floorMapEntity?.rotationCoords[0]);
        const point2 = flipCoords(this.floorMapEntity?.rotationCoords[1]);
        const point3 = flipCoords(this.floorMapEntity?.rotationCoords[2]);

        const imgSrc = 'data:image/png;base64,' + this._arrayBufferToBase64(response);

        // Ensure floorplan-pane exists above Three.js canvas (650)
        if (!this.floorMap.getPane('floorplan-pane')) {
          this.floorMap.createPane('floorplan-pane');
          const floorplanPane = this.floorMap.getPane('floorplan-pane');
          if (floorplanPane) {
            floorplanPane.style.zIndex = '660';
          }
        }

        // @ts-ignore - .rotated() is provided by the leaflet-imageoverlay-rotated plugin
        const overlay = L.imageOverlay.rotated(imgSrc, point1, point2, point3, {
          opacity: 1.0,
          interactive: false,
          pane: 'floorplan-pane'
        });
        this.floorPlanOverlay = overlay;
        // Only add to map if it should be visible
        if (this.floorPlanOverlayVisible) {
          overlay.addTo(this.floorMap);
        }
      }
    }).catch(() => { });
  }

  private _arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  getFloorUserInBBoxAsCluster(): void {

    const incidentId = this.cameraService.getCurrentIncidentId();
    if (!incidentId || !this.data.show3DUserClusters) {
      this.updateUserClusterIn3D([]);
      return;
    }

    let bbox: number[] | undefined;
    const beacons = this.getBeaconsonList();
    const lats = _.map(beacons, (o) => o.position.coordinates[1]);
    const lons = _.map(beacons, (o) => o.position.coordinates[0]);
    if (lats.length > 0) {
      let latMin = _.min(lats);
      let latMax = _.max(lats);
      let lonMin = _.min(lons);
      let lonMax = _.max(lons);
      if (lats.length === 1) {
        latMin -= 0.00001;
        lonMin -= 0.00001;
        latMax += 0.00001;
        lonMax += 0.00001;
      }
      bbox = [lonMin, latMin, lonMax, latMax];
    }
    if (!bbox && this.floorMapEntity?.locations) {
      const lats = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[0] : (o as any).lat));
      const lons = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[1] : (o as any).lng));
      if (lats.length > 0) {
        bbox = [_.min(lons), _.min(lats), _.max(lons), _.max(lats)];
      }
    }
    const floorPerimeterId = this.floorMapEntity.perimeterId;
    const floorType = 'FLOOR';
    const statuses: any[] = [];
    if (this.data.dangerChecked) {
      statuses.push('DANGER');
    }
    if (this.data.safeChecked) {
      statuses.push('SAFE');
    }
    if (this.data.unknownChecked) {
      statuses.push('UNKNOWN');
    }
    if (this.data.suspectChecked) {
      statuses.push('SUSPECT');
    }
    if (statuses.length === 0) {
      statuses.push('DANGER', 'SAFE', 'UNKNOWN');
    }
    const floorUserRequest: FloorUsersRequest = {
      gridSize: 50,
      threshold: 0,
      bbox: bbox ?? [0, 0, 0, 0],
      floorPerimeterId: floorPerimeterId,
      floorType: floorType,
      incidentId: incidentId,
      statuses: statuses
    };
    this.dataService.findOrClusterUsersByBbox(floorUserRequest).subscribe({
      next: (results: any) => {
        const combinedData = [...(results?.clusters || []), ...(results?.users || [])];
        this.updateUserClusterIn3D(combinedData, statuses);
      },
      error: () => {
        // Optionally log error
        // console.error(error);
      },
    });
  }
  updateUserClusterIn3D(
    data: any[],
    statuses?: string[],
    timestamp?: any
  ): void {
    if (_.isEqual(data, this.last3DUserData)) {
      return;
    }
    this.updatePerimeterLayers()
    this.removeUserClusterIn3D();
    this.last3DUserData = data;

    if (!data || data.length === 0 || this.stopPoller) {
      return;
    }

    statuses = statuses ? statuses : [];
    timestamp = timestamp ? timestamp : '';

    this.dataSourceUserIn3D = new Cesium.CustomDataSource('user-3d-icons');
    this.viewer.dataSources.add(this.dataSourceUserIn3D);
    const altitude = this.floorMapEntity?.extrudedHeight + 6;
    _.forEach(data, (d: any) => {
      const lat = d.position.coordinates[1];
      const lon = d.position.coordinates[0];
      const safe = d.safe ? parseInt(d.safe) : 0;
      const danger = d.danger ? parseInt(d.danger) : 0;
      const unknown = d.unknown ? parseInt(d.unknown) : 0;
      const suspect = d.suspect ? parseInt(d.suspect) : 0;
      const active = d.active ? parseInt(d.active) : 0;

      const total = safe + danger + unknown + suspect + active;
      let displayTotal = total;
      if (total === 0) {
        displayTotal = 1;
      }
      const pinBuilder = new Cesium.PinBuilder();
      let color = new Cesium.Color(0.25, 0.75, 0.25, 1); // safe

      const statusStr = (d.status?.toString() || '').toUpperCase();

      if (danger > 0 || active > 0 || statusStr === 'DANGER' || statusStr === '2' || statusStr === 'ACTIVE') {
        color = new Cesium.Color(0.75, 0.25, 0.25, 1); // danger
      } else if (suspect > 0 || statusStr === 'SUSPECT' || statusStr === '3') {
        color = new Cesium.Color(0.7, 0.3, 0.9, 1.0); // suspect (Purple)
      } else if (unknown > 0 || statusStr === 'UNKNOWN' || statusStr === 'STREET' || statusStr === '1') {
        color = new Cesium.Color(0.75, 0.75, 0.25, 1); // unknown
      } else if (safe > 0 || statusStr === 'SAFE' || statusStr === '0') {
        color = new Cesium.Color(0.25, 0.75, 0.25, 1); // safe
      }

      const pin = pinBuilder.fromText(displayTotal.toString(), color, 48).toDataURL();
      this.dataSourceUserIn3D.entities.add({
        position: Cesium.Cartesian3.fromDegrees(lon, lat, altitude),
        billboard: {
          image: pin,
        },
        data: d,
        statuses: statuses,
        timestamp: timestamp,
        name: 'userClucterIn3D',
      });
    });
  }
  removeUserClusterIn3D(): void {
    if (this.dataSourceUserIn3D && this.viewer) {
      this.dataSourceUserIn3D.entities.removeAll();
      this.viewer.dataSources.remove(this.dataSourceUserIn3D, true);
      this.dataSourceUserIn3D = null;
      this.viewer.scene.requestRender();
    }
  }

  private resolveAndLoadGlbOnCesium(): void {
    this.blenderFloorMap.resolveAndLoadGlbOnCesium({
      viewer: this.viewer,
      floorMapEntity: this.floorMapEntity,
      assetBundle: this.assetBundle,
      assetResolutionKey: this.assetResolutionKey,
    });
  }

  getFloorUserInBBoxPlayback(): void {
    const incidentId = this.sessionOptionService.getSessionData()?.event?.id
    // const incidentId = this.floorMapEntity.eventId;
    if (!incidentId) {
      this.updateLeafFloorUserDeviceMarkers([]);
      return;
    }

    let bbox: number[] | undefined;
    const beacons = this.getBeaconsonList();
    const lats = _.map(beacons, (o) => o.position.coordinates[1]);
    const lons = _.map(beacons, (o) => o.position.coordinates[0]);
    if (lats.length > 0) {
      let latMin = _.min(lats);
      let latMax = _.max(lats);
      let lonMin = _.min(lons);
      let lonMax = _.max(lons);
      if (lats.length === 1) {
        latMin -= 0.00001;
        lonMin -= 0.00001;
        latMax += 0.00001;
        lonMax += 0.00001;
      }
      bbox = [lonMin, latMin, lonMax, latMax];
    }

    if (!bbox && this.floorMapEntity?.locations) {
      const lats = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[0] : (o as any).lat));
      const lons = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[1] : (o as any).lng));
      if (lats.length > 0) {
        bbox = [_.min(lons), _.min(lats), _.max(lons), _.max(lats)];
      }
    }

    if (!bbox) {
      return;
    }

    const floorPerimeterId = this.floorMapEntity.perimeterId;
    const floorType = 'FLOOR';
    const statuses: any[] = [];
    if (this.data.dangerChecked) {
      statuses.push('DANGER');
    }
    if (this.data.safeChecked) {
      statuses.push('SAFE');
    }
    if (this.data.unknownChecked) {
      statuses.push('UNKNOWN');
    }
    if (this.data.suspectChecked) {
      statuses.push('SUSPECT');
    }

    if (statuses.length === 0 && this.data.users.length === 0) {
      return;
    }

    const timestamp = Math.floor((this.coresService.getCurrentTime() ?? Date.now()) / 1000) * 1000;
    const timestampStatus = timestamp + statuses.toString();


    this.dataService.getFloorUserInBBoxPlayback(
      incidentId,
      timestamp,
      this.data.gridSize,
      this.data.threshold,
      bbox,
      floorPerimeterId,
      floorType,
      statuses
    ).subscribe({
      next: (results) => {
        const rows = [];

        if (results && results.users && results.users.length > 0) {
          rows.push(...results.users);
        } else if (results && results.clusters && results.clusters.length > 0) {
          rows.push(...results.clusters);
        }

        this.updateLeafFloorUserDeviceMarkers(rows, statuses);
        this.updateUserListsFromResults(rows);

      },
      error: (error) => {
        console.error('Error fetching playback floor user data:', error);
      }
    });
  }

  getFloorUserInBBoxAsClusterPlayback(): void {
    const incidentId = this.sessionOptionService.getSessionData()?.event?.id
    // const incidentId = this.floorMapEntity.eventId;
    if (!incidentId || !this.data.show3DUserClusters) {
      this.updateUserClusterIn3D([]);
      return;
    }

    let bbox: number[] | undefined;
    const beacons = this.getBeaconsonList();
    const lats = _.map(beacons, (o) => o.position.coordinates[1]);
    const lons = _.map(beacons, (o) => o.position.coordinates[0]);
    if (lats.length > 0) {
      let latMin = _.min(lats);
      let latMax = _.max(lats);
      let lonMin = _.min(lons);
      let lonMax = _.max(lons);
      if (lats.length === 1) {
        latMin -= 0.00001;
        lonMin -= 0.00001;
        latMax += 0.00001;
        lonMax += 0.00001;
      }
      bbox = [lonMin, latMin, lonMax, latMax];
    }

    if (!bbox && this.floorMapEntity?.locations) {
      const lats = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[0] : (o as any).lat));
      const lons = _.map(this.floorMapEntity.locations, (o) => (Array.isArray(o) ? o[1] : (o as any).lng));
      if (lats.length > 0) {
        bbox = [_.min(lons), _.min(lats), _.max(lons), _.max(lats)];
      }
    }

    if (!bbox) {
      return;
    }

    const floorPerimeterId = this.floorMapEntity.perimeterId;
    const floorType = 'FLOOR';
    const statuses: any[] = [];
    if (this.data.dangerChecked) {
      statuses.push('DANGER');
    }
    if (this.data.safeChecked) {
      statuses.push('SAFE');
    }
    if (this.data.unknownChecked) {
      statuses.push('UNKNOWN');
    }
    if (this.data.suspectChecked) {
      statuses.push('SUSPECT');
    }

    if (statuses.length === 0 && this.data.users.length === 0) {
      return;
    }

    const timestamp = Math.floor((this.coresService.getCurrentTime() ?? Date.now()) / 1000) * 1000;

    this.dataService.getFloorUserInBBoxPlayback(
      incidentId,
      timestamp,
      this.data.gridSize,
      0,
      bbox,
      floorPerimeterId,
      floorType,
      statuses
    ).subscribe({
      next: (results) => {
        // Update 3D clusters on the building
        this.updateUserClusterIn3D(results.clusters || [], statuses, timestamp);
        // Also update right-side drawer user list from users array
        const users = results.users || [];
        if (users.length > 0) {
          this.updateUserListsFromResults(users);
        }
      },
      error: (error) => {
        console.error('Error fetching playback cluster data:', error);
      }
    });
  }

  // Update the poller method to handle playback
  pollerPlayback(): void {
    this.getFloorUserInBBoxPlayback();
    this.getFloorUserInBBoxAsClusterPlayback();
    this.updatePerimeterLayers();
  }


  onStatusChanged() {

    this.data.dangerChecked = this.dangerChecked;
    this.data.safeChecked = this.safeChecked;
    this.data.unknownChecked = this.unknownChecked;
    this.data.suspectChecked = this.suspectChecked;
    this.data.page = 1;
    if (this.enablePlayback) {
      this.pollerPlayback();
    } else {
      this.getFloorUserInBBox();
    }
  }

  ngAfterViewInit(): void {
    // Initialize Three.js after view is ready
    setTimeout(() => {
      this.initializeThreeJsFloorMap();
    }, 500);
  }

  /**
   * Initialize Three.js rendering for the floor map in component's own container
   */
  private initializeThreeJsFloorMap(): void {
    if (!this.canInitializeThreeJs()) {
      this.cleanupThreeJs();
      this.isLoading = false;
      return;
    }

    // If already initialized, update and re-render floor in Three.js
    if (this.threeJsInitialized && this.threeRenderer && this.threeScene) {
      this.syncThreeJsWithLeaflet();
      this.renderFloorInThreeJs();
      this.beaconOnChange();
      this.isLoading = false;
      return;
    }

    const container = this.floorMapElement.nativeElement;

    try {
      const { width, height } = this.getContainerSize(container);

      this.createScene();
      this.createCamera(width, height);
      const canvas = this.createRenderer(container, width, height);

      this.createRaycaster();
      this.setupMouseHandlers(canvas);
      this.setupControls(canvas);
      this.setupLights();

      this.syncThreeJsWithLeaflet();
      this.renderFloorInThreeJs();
      this.startAnimationLoop();

      this.setupResizeHandler();
      this.setupLeafletSyncListeners();

      setTimeout(() => this.syncThreeJsWithLeaflet(), 100);

      this.threeJsInitialized = true;
      this.beaconOnChange(); // Apply ThreeJS beacon visibility settings (hiding Leaflet ones)
    } catch (err) {
      console.error('ThreeJS Floor init error:', err);
      this.threeJsInitialized = false;
      this.isLoading = false;
    }
  }

  private canInitializeThreeJs(): boolean {
    return !!this.floorMapEntity && !!this.floorMapElement?.nativeElement;
  }

  private cleanupExistingCanvas(container: HTMLElement): void {
    const existingCanvas = container.querySelector('canvas');
    if (existingCanvas) {
      existingCanvas.remove();
    }
  }

  private getContainerSize(container: HTMLElement) {
    return {
      width: container.clientWidth || 800,
      height: container.clientHeight || 600
    };
  }

  private createScene(): void {
    this.threeScene = new THREE.Scene();
    this.threeScene.background = null;
  }

  private createCamera(width: number, height: number): void {
    const initialSize = 100;

    this.threeCamera = new THREE.OrthographicCamera(
      -initialSize * (width / height),
      initialSize * (width / height),
      initialSize,
      -initialSize,
      0.1,
      10000
    );

    this.threeCamera.position.set(0, 500, 0);
    this.threeCamera.lookAt(0, 0, 0);
    this.threeCamera.up.set(0, 0, 1);
  }

  private createRenderer(
    container: HTMLElement,
    width: number,
    height: number
  ): HTMLCanvasElement {

    // Create renderer ONLY once globally
    if (!FloorMapComponent.globalRenderer) {

      FloorMapComponent.globalRenderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        logarithmicDepthBuffer: true
      });

      FloorMapComponent.globalRenderer.setClearColor(0x000000, 0);
    }

    // Reuse global renderer
    this.threeRenderer = FloorMapComponent.globalRenderer;

    this.threeRenderer.setSize(width, height);

    const canvas = this.threeRenderer.domElement;

    // Prevent duplicate append
    if (!container.contains(canvas)) {
      container.appendChild(canvas);
    }

    Object.assign(canvas.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      zIndex: '650',
      pointerEvents: 'auto',
      cursor: 'grab'
    });

    this.applyThreeOverlayVisibility();

    return canvas;
  }

  private applyThreeOverlayVisibility(): void {
    const canvas = this.threeRenderer?.domElement;
    if (!canvas) {
      return;
    }
    const hidden = this.suppressThreeOverlay;
    canvas.style.display = hidden ? 'none' : 'block';
    canvas.style.visibility = hidden ? 'hidden' : 'visible';
    canvas.style.pointerEvents = hidden ? 'none' : 'auto';
  }


  private createRaycaster(): void {
    this.threeRaycaster = new THREE.Raycaster();
  }

  private setupMouseHandlers(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('mousemove', this.onMouseMove(canvas));
    canvas.addEventListener('mousedown', this.onMouseDown(canvas));
    canvas.addEventListener('click', this.onCanvasClick(canvas));
  }

  private onMouseMove(canvas: HTMLCanvasElement) {
    return (event: MouseEvent) => {
      if (
        !this.threeScene ||
        !this.threeCamera ||
        !this.threeRaycaster ||
        !this.threeRenderer
      ) {
        return;
      }

      const mouse = this.getNormalizedMouse(event, canvas);
      this.threeRaycaster.setFromCamera(mouse, this.threeCamera);

      const intersects = this.threeRaycaster.intersectObjects(
        this.threeScene.children,
        true
      );

      const isOverInteractive = this.isOverInteractiveObject(intersects);

      canvas.style.cursor = isOverInteractive ? 'pointer' : 'grab';
      this.threeControls && (this.threeControls.enabled = isOverInteractive);

      // Hover Room Label Tooltip logic
      let hoveredRoomObj: any = null;
      for (const intersect of intersects) {
        let obj: any = intersect.object;
        let depth = 0;
        while (obj && depth < 10) {
          if (obj.userData?.isRoomMesh) {
            hoveredRoomObj = obj;
            break;
          }
          obj = obj.parent;
          depth++;
        }
        if (hoveredRoomObj) break;
      }

      if (hoveredRoomObj && !this.showBeacons) {
        const roomId = hoveredRoomObj.userData.roomId || hoveredRoomObj.userData.id;
        if (this.hoveredRoomId !== roomId) {
          this.clearRoomTooltip();
          this.hoveredRoomId = roomId;
          const roomLabel = hoveredRoomObj.userData.roomLabel || hoveredRoomObj.userData.label || 'Room';
          const centerLat = hoveredRoomObj.userData.centerLat;
          const centerLon = hoveredRoomObj.userData.centerLon;
          if (centerLat !== undefined && centerLon !== undefined) {
            this.showRoomTooltip(centerLat, centerLon, roomLabel);
          }
        }
      } else {
        if (this.hoveredRoomId !== null) {
          this.hoveredRoomId = null;
          this.clearRoomTooltip();
        }
      }
    };
  }

  private onMouseDown(canvas: HTMLCanvasElement) {
    return (event: MouseEvent) => {
      if (!this.threeScene || !this.threeCamera || !this.threeRaycaster) {
        return;
      }

      const mouse = this.getNormalizedMouse(event, canvas);
      this.threeRaycaster.setFromCamera(mouse, this.threeCamera);

      const intersects = this.threeRaycaster.intersectObjects(
        this.threeScene.children,
        true
      );

      const isOver3D = this.isOver3DObject(intersects);

      this.threeControls && (this.threeControls.enabled = isOver3D);

      if (isOver3D) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
  }

  private onCanvasClick(canvas: HTMLCanvasElement) {
    return (event: MouseEvent) => {
      if (
        !this.threeScene ||
        !this.threeCamera ||
        !this.threeRaycaster ||
        !this.threeControls?.enabled
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const mouse = this.getNormalizedMouse(event, canvas);
      this.threeRaycaster.setFromCamera(mouse, this.threeCamera);

      const intersects = this.threeRaycaster.intersectObjects(
        this.threeScene.children,
        true
      );

      const target = this.findClickableObject(intersects);
      this.handleObjectClick(target);
    };
  }

  private getNormalizedMouse(event: MouseEvent, canvas: HTMLCanvasElement): THREE.Vector2 {
    const rect = canvas.getBoundingClientRect();
    return new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
  }

  private isOverInteractiveObject(intersects: THREE.Intersection[]): boolean {
    return this.findParentWithUserData(intersects, [
      'isRoomMesh',
      'isBeacon'
    ]);
  }

  private isOver3DObject(intersects: THREE.Intersection[]): boolean {
    return this.findParentWithUserData(intersects, [
      'isRoomMesh',
      'isFloorMesh',
      'isBeacon'
    ]);
  }

  private findClickableObject(intersects: THREE.Intersection[]): any {
    for (const intersect of intersects) {
      let obj: any = intersect.object;
      let depth = 0;

      while (obj && depth < 10) {
        if (
          obj.userData?.isRoomMesh ||
          obj.userData?.isBeacon ||
          obj.userData?.isUser ||
          obj.userData?.isUserCount
        ) {
          return obj;
        }
        obj = obj.parent;
        depth++;
      }
    }
    return null;
  }

  private findParentWithUserData(
    intersects: THREE.Intersection[],
    keys: string[]
  ): boolean {
    for (const intersect of intersects) {
      let obj: any = intersect.object;
      let depth = 0;

      while (obj && depth < 10) {
        if (keys.some(k => obj.userData?.[k])) {
          return true;
        }
        obj = obj.parent;
        depth++;
      }
    }
    return false;
  }

  private setupControls(canvas: HTMLCanvasElement): void {
    this.threeControls = new OrbitControls(
      this.getActiveCamera(),
      canvas
    );
    this.threeControls.enableDamping = true;
    this.threeControls.dampingFactor = 0.05;
    this.threeControls.enableZoom = true;
    this.threeControls.enablePan = true;
    this.threeControls.enableRotate = true;
    this.threeControls.minDistance = 10;
    this.threeControls.maxDistance = 2000;
    this.threeControls.enabled = false;
  }

  private lightsGroup: THREE.Group = new THREE.Group();

  private setupLights(isBlenderView?: boolean): void {
    if (!this.threeScene) return;

    const isBlender = isBlenderView !== undefined
      ? isBlenderView
      : (this.blenderFloorMap?.isGlbModelLoaded() ?? false);

    if (isBlender) {
      // Blender View lighting setup via Blender Floor Map service
      this.blenderFloorMap?.setupBlenderLighting(this.threeScene, this.lightsGroup);
    } else {
      // Normal Three.js View lighting (Original setup)
      if (!this.threeScene.children.includes(this.lightsGroup)) {
        this.threeScene.add(this.lightsGroup);
      }
      this.lightsGroup.clear();

      this.lightsGroup.add(new THREE.AmbientLight(0x404040, 1.5));

      const dir = new THREE.DirectionalLight(0xffffff, 1);
      dir.position.set(100, 200, 100);
      this.lightsGroup.add(dir);
    }
  }

  private setupResizeHandler(): void {
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  private getActiveCamera(): THREE.Camera {
    if (!this.threeCamera) {
      throw new Error('Three camera not initialized');
    }
    return this.threeCamera;
  }

  private handleObjectClick(targetObject: any): void {
    if (!targetObject || !this.floorMap) {
      this.clearRoomTooltip();
      return;
    }

    let label: string | undefined;
    let centerLat = targetObject.userData?.centerLat;
    let centerLon = targetObject.userData?.centerLon;

    /* ---------- USER CLUSTER ---------- */
    if (targetObject.userData?.isUser) {
      const clusterData = targetObject.userData.clusterData || [];
      const allUsers: any[] = [];

      clusterData.forEach((cluster: any) => {
        if (cluster?.data?.users?.length) {
          allUsers.push(...cluster.data.users);
        }
      });

      if (allUsers.length === 1) {
        const user = allUsers[0];
        label = user.name || 'User';

        if (user.position?.coordinates?.length === 2) {
          centerLon = user.position.coordinates[0];
          centerLat = user.position.coordinates[1];
        }
      }

      if (this.userClusterModalRef) {
        this.userClusterModalRef.close();
      }

      this.userClusterModalRef = this.modalService.open(
        UserClusterListUiComponent,
        {
          backdrop: false,
          keyboard: false,
          windowClass: 'modal-class'
        }
      );

      const component = this.userClusterModalRef.componentInstance;
      component.userData = allUsers;
      component.statuses = targetObject?.userData?.statuses;
      component.isCluster = true;
      component.entity = this.floorMapEntity;
    }

    /* ---------- ROOM ---------- */
    if (targetObject.userData?.isRoomMesh) {
      const roomLabel =
        targetObject.userData.roomLabel ||
        targetObject.userData.label ||
        'Room';

      const roomId = targetObject.userData.roomId || targetObject.userData.id;
      const roomPerimeterId = targetObject.userData.perimeterId;
      const roomLocations = targetObject.userData.locations || [];

      // Debounce: ignore rapid clicks on the same room
      const currentTime = Date.now();
      const clickDebounceMs = 300;
      if (this.lastClickedRoomId === roomId && (currentTime - this.lastClickTime) < clickDebounceMs) {
        return; // Ignore duplicate click
      }
      this.lastClickedRoomId = roomId;
      this.lastClickTime = currentTime;

      // Show room label tooltip at room centroid
      if (Array.isArray(roomLocations) && roomLocations.length > 0) {
        const lats = roomLocations
          .map((loc: number[]) => loc[0])
          .filter((n: number) => typeof n === 'number');
        const lons = roomLocations
          .map((loc: number[]) => loc[1])
          .filter((n: number) => typeof n === 'number');
        if (lats.length && lons.length) {
          const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
          const centerLon = lons.reduce((a, b) => a + b, 0) / lons.length;
          this.showRoomTooltip(centerLat, centerLon, roomLabel);
        }
      }

      return;
      // Open draggable room popup
      // this.openRoomPopup({
      //   label: roomLabel,
      //   id: roomId,
      //   perimeterId: roomPerimeterId,
      //   locations: roomLocations,
      //   users: targetObject.userData.users || [],
      //   cameras: targetObject.userData.cameras || [],
      //   beacons: targetObject.userData.beacons || [],
      // });

      return; // Don't show tooltip, show popup instead
    }

    /* ---------- BEACON ---------- */
    if (targetObject.userData?.isBeacon) {
      const beaconLabel =
        targetObject.userData.beaconLabel ||
        targetObject.userData.label ||
        'Beacon';

      const beaconValue = targetObject.userData.beaconValue;
      label = beaconValue ? `${beaconLabel}: ${beaconValue}` : beaconLabel;

      const beaconId = targetObject.userData.beaconId;
      const beaconLatLng = this.getBeaconLatLngById(beaconId);

      if (!beaconLatLng) {
        return;
      }

      centerLat = beaconLatLng.lat;
      centerLon = beaconLatLng.lon;
    }

    if (
      typeof centerLat !== 'number' ||
      typeof centerLon !== 'number'
    ) {
      return;
    }

    this.showRoomTooltip(centerLat, centerLon, label);
  }

  private clearRoomTooltip(): void {
    if (this.roomTooltip && this.floorMap) {
      this.floorMap.removeLayer(this.roomTooltip);
      this.roomTooltip = null;
    }
  }



  private showRoomTooltip(
    lat: number,
    lon: number,
    label?: string
  ): void {
    this.clearRoomTooltip();

    this.roomTooltip = L.tooltip({
      direction: 'center',
      permanent: false,
      className: 'room-label-tooltip',
      opacity: 1
    })
      .setLatLng([lat, lon])
      .setContent(label || '')
      .addTo(this.floorMap!);

    const el = (this.roomTooltip as any)._container as HTMLElement;
    if (el) {
      el.style.zIndex = '2000';
      el.style.pointerEvents = 'none';
    }
  }

  private getBeaconLatLngById(
    beaconId: string
  ): { lat: number; lon: number } | null {

    const beacons = this.getBeaconsonList();
    const beacon = beacons.find(b => b.id === beaconId);
    if (!beacon || !beacon.position?.coordinates) {
      return null;
    }

    const coords = beacon.position.coordinates;

    // EXPECTED FORMAT: [lon, lat]
    if (!Array.isArray(coords) || coords.length < 2) {
      return null;
    }

    const [lon, lat] = coords;

    if (typeof lat !== 'number' || typeof lon !== 'number') {
      return null;
    }

    return { lat, lon };
  }

  /**
   * Handle window resize
   */
  private onWindowResize(): void {
    if (!this.floorMapElement || !this.threeRenderer || !this.threeCamera) {
      return;
    }

    const container = this.floorMapElement.nativeElement;
    const width = container.clientWidth;
    const height = container.clientHeight;

    if (this.threeCamera instanceof THREE.OrthographicCamera) {
      // For orthographic camera, update the size based on current view
      const aspect = width / height;
      const currentSize = this.threeCamera.top;
      this.threeCamera.left = -currentSize * aspect;
      this.threeCamera.right = currentSize * aspect;
      this.threeCamera.updateProjectionMatrix();
    } else if (this.threeCamera instanceof THREE.PerspectiveCamera) {
      this.threeCamera.aspect = width / height;
      this.threeCamera.updateProjectionMatrix();
    }

    this.threeRenderer.setSize(width, height);

    // Re-sync with Leaflet after resize
    if (this.floorMap) {
      setTimeout(() => {
        this.syncThreeJsWithLeaflet();
      }, 50);
    }
  }

  /**
   * Convert floorMapEntity data to GeoJSON format for Three.js
   */
  private convertFloorToGeoJSON(): any[] {
    const geojsonArray: any[] = [];

    if (!this.floorMapEntity) {
      return geojsonArray;
    }

    // Add the main floor
    if (this.floorMapEntity.locations && Array.isArray(this.floorMapEntity.locations)) {
      const floorGeoJSON: any = {
        id: this.floorMapEntity.perimeterId || this.floorMapEntity.id,
        label: this.floorMapEntity.label || 'Floor',
        type: 'FLOOR',
        geometry: {
          type: 'Polygon',
          coordinates: [this.floorMapEntity.locations.map((loc: number[]) => {
            // Ensure coordinates are in [lon, lat] format
            if (Array.isArray(loc) && loc.length >= 2) {
              return [loc[1], loc[0]]; // Convert [lat, lon] to [lon, lat]
            }
            return loc;
          })]
        },
        ancestors: this.floorMapEntity.ancestors || [],
        descendants: this.floorMapEntity.descendants || []
      };
      geojsonArray.push(floorGeoJSON);
    }

    // Add perimeters (rooms) if available - filter out floor perimeters
    if (this.floorMapEntity.perimeters && typeof this.floorMapEntity.perimeters === 'object') {
      const allPerimeters = Object.values(this.floorMapEntity.perimeters);
      // Filter out floor perimeters (perimeterType === 'FLOOR' or type === 'FLOOR')
      const roomPerimeters = allPerimeters.filter((p: any) => {
        return p && p.perimeterType !== 'FLOOR' && p.type !== 'FLOOR';
      });
      roomPerimeters.forEach((perimeter: any) => {
        if (perimeter && perimeter.locations && Array.isArray(perimeter.locations)) {
          const roomGeoJSON: any = {
            id: perimeter.id,
            label: perimeter.name || perimeter.label || 'Room',
            type: perimeter.type || 'ROOM',
            geometry: {
              type: 'Polygon',
              coordinates: [perimeter.locations.map((loc: number[]) => {
                // Ensure coordinates are in [lon, lat] format
                if (Array.isArray(loc) && loc.length >= 2) {
                  return [loc[1], loc[0]]; // Convert [lat, lon] to [lon, lat]
                }
                return loc;
              })]
            },
            ancestors: perimeter.ancestors || [this.floorMapEntity.perimeterId || this.floorMapEntity.id],
            descendants: perimeter.descendants || []
          };
          geojsonArray.push(roomGeoJSON);
        }
      });
    }

    return geojsonArray;
  }

  /**
   * Render the floor in Three.js
   */
  private renderFloorInThreeJs(): void {
    if (!this.floorMapEntity || !this.threeScene) {
      return;
    }

    // Clear existing floor meshes
    const objectsToRemove: THREE.Object3D[] = [];
    this.threeScene.traverse((object) => {
      if (object.userData['isFloorMesh'] || object.userData['isRoomMesh']) {
        objectsToRemove.push(object);
      }
    });
    objectsToRemove.forEach(obj => this.threeScene!.remove(obj));

    let floorLocations = this.floorMapEntity.locations;
    if (!floorLocations && this.floorMapEntity.geometry?.coordinates?.[0]) {
      floorLocations = this.floorMapEntity.geometry.coordinates[0].map((c: any) => [c[1], c[0]]);
    }
    if (!floorLocations && this.floorMapEntity.perimeters && typeof this.floorMapEntity.perimeters === 'object') {
      const allPerims = Object.values(this.floorMapEntity.perimeters);
      const floorPerim = allPerims.find((p: any) => p && (p.perimeterType === 'FLOOR' || p.type === 'FLOOR'));
      if (floorPerim) {
        floorLocations = (floorPerim as any).locations || (floorPerim as any).geometry?.coordinates?.[0]?.map((c: any) => [c[1], c[0]]);
      }
    }

    const centerLat = this.floorMapEntity.centerpoint?.latitude ||
      (floorLocations && floorLocations[0] ? floorLocations[0][0] : 0);
    const centerLon = this.floorMapEntity.centerpoint?.longitude ||
      (floorLocations && floorLocations[0] ? floorLocations[0][1] : 0);

    // Render main floor
    if (floorLocations && Array.isArray(floorLocations) && floorLocations.length >= 3) {
      const floorMesh = this.createFloorMesh(floorLocations, centerLat, centerLon);
      if (floorMesh) {
        floorMesh.userData['isFloorMesh'] = true;
        floorMesh.renderOrder = -1; // Render behind room meshes
        this.threeScene.add(floorMesh);
      }
    }

    // Render perimeters (rooms) - filter out floor perimeters, only render room perimeters
    if (this.floorMapEntity.perimeters && typeof this.floorMapEntity.perimeters === 'object') {
      const allPerimeters = Object.values(this.floorMapEntity.perimeters);
      // Filter out floor perimeters (perimeterType === 'FLOOR' or type === 'FLOOR')
      const roomPerimeters = allPerimeters.filter((p: any) => {
        return p && p.perimeterType !== 'FLOOR' && p.type !== 'FLOOR';
      });
      roomPerimeters.forEach((perimeter: any) => {
        let roomLocs = perimeter?.locations;
        if (!roomLocs && perimeter?.geometry?.coordinates?.[0]) {
          roomLocs = perimeter.geometry.coordinates[0].map((c: any) => [c[1], c[0]]);
        }
        if (perimeter && roomLocs && Array.isArray(roomLocs) && roomLocs.length >= 3) {
          const roomMesh = this.createRoomMesh(roomLocs, centerLat, centerLon, perimeter.id);
          if (roomMesh && this.threeScene) {
            roomMesh.userData['isRoomMesh'] = true;
            roomMesh.userData['roomId'] = perimeter.id;

            // Store room label for display
            roomMesh.userData['roomLabel'] = perimeter.name || perimeter.label || `Room ${perimeter.id}`;

            // Calculate center coordinates of the room
            let sumLat = 0;
            let sumLon = 0;
            let count = 0;
            roomLocs.forEach((loc: number[]) => {
              if (Array.isArray(loc) && loc.length >= 2) {
                sumLat += loc[0]; // lat
                sumLon += loc[1]; // lon
                count++;
              }
            });
            if (count > 0) {
              roomMesh.userData['centerLat'] = sumLat / count;
              roomMesh.userData['centerLon'] = sumLon / count;
            }

            // Position room mesh slightly above floor mesh to ensure it's hit first by raycaster
            roomMesh.position.y = 0.1;
            roomMesh.renderOrder = 1; // Render on top of floor

            this.threeScene.add(roomMesh);
          }
        }
      });
    }

    // Render beacons if enabled
    if (this.showBeacons) {
      this.renderBeaconsInThreeJs();
    }
    else {
      // When beacons are disabled, render user badges only
      // Check required dependencies before rendering users
      if (this.floorMapEntity && this.threeScene && this.threeCamera &&
        this.threeRenderer && this.floorMap && this.clusterMarkers) {
        setTimeout(() => {
          this.refreshUserBadgesAfterBeaconRender();
        }, 50);
      }
    }

    // Clear old user badges when floor is re-rendered
    // Users will be re-rendered when new data arrives via updateLeafFloorUserDeviceMarkers
    this.clearUsersInThreeJs();

    // Adjust camera to fit the floor
    this.fitCameraToFloor();

    // Floor rendering is complete, turn off loading indicator
    this.isLoading = false;
  }

  /**
   * Render beacons in Three.js scene
   * Places a Bluetooth icon at the actual beacon positions from the beacon list
   */
  private renderBeaconsInThreeJs(): void {
    if (!this.floorMapEntity || !this.threeScene || !this.threeCamera) {
      return;
    }

    const scene = this.threeScene;
    const camera = this.threeCamera;

    // Clear existing beacon sprites
    const beaconsToRemove: THREE.Object3D[] = [];
    scene.traverse((object) => {
      if (object.userData['isBeacon']) {
        beaconsToRemove.push(object);
      }
    });
    beaconsToRemove.forEach(obj => {
      if (obj instanceof THREE.Sprite) {
        if (obj.material instanceof THREE.SpriteMaterial && obj.material.map) {
          obj.material.map.dispose();
        }
        if (obj.material) {
          obj.material.dispose();
        }
      }
      scene.remove(obj);
    });

    const centerLat = this.floorMapEntity.centerpoint?.latitude || 0;
    const centerLon = this.floorMapEntity.centerpoint?.longitude || 0;

    // Load Bluetooth icon texture (shared across all beacons)
    const textureLoader = new THREE.TextureLoader();
    const bluetoothTexture = textureLoader.load('assets/img/arcangel/bluetooth.png',
      (texture) => {
        texture.needsUpdate = true;
      },
      undefined,
      (error) => {
        console.error('Error loading bluetooth icon:', error);
      }
    );

    // Get actual beacon list from floorMapEntity (not perimeters)
    const beaconsList = this.getBeaconsonList();

    // Calculate sprite scale once for all beacons to ensure they all have the same size
    // For orthographic camera, convert pixel size to world units based on viewport
    let spriteScale = this.BEACON_SPRITE_SIZE;
    if (this.threeCamera instanceof THREE.OrthographicCamera && this.threeRenderer) {
      // Get camera's visible height in world units
      const cameraHeight = this.threeCamera.top - this.threeCamera.bottom;
      // Get renderer's height in pixels
      const rendererHeight = this.threeRenderer.domElement.height;
      // Convert desired pixel size to world units: (pixelSize / rendererHeight) * cameraHeight
      spriteScale = (this.BEACON_SPRITE_SIZE / rendererHeight) * cameraHeight;
    }

    // Place a Bluetooth icon at the center of each beacon's perimeter/room
    beaconsList.forEach((beacon: any) => {
      if (!beacon) {
        return; // Skip invalid beacons
      }

      let localPos: THREE.Vector2 | null = null;

      // If beacon has a perimeter, find the actual rendered room mesh and use its bounding box center
      // This guarantees perfect alignment with the rendered mesh position
      if (beacon.perimeter && beacon.perimeter.id && this.threeScene) {
        // Find the room mesh in the scene by matching perimeter ID
        let roomMesh: THREE.Object3D | null = null;
        this.threeScene.traverse((object) => {
          if (object.userData['isRoomMesh'] && object.userData['roomId'] === beacon.perimeter.id) {
            roomMesh = object;
          }
        });

        if (roomMesh) {
          const mesh = roomMesh as THREE.Mesh;
          if (mesh instanceof THREE.Mesh && mesh.geometry) {
            // Ensure bounding box is computed
            mesh.geometry.computeBoundingBox();
            const bbox = mesh.geometry.boundingBox;

            if (bbox) {
              // Get center in local mesh coordinates
              const meshCenter = new THREE.Vector3();
              bbox.getCenter(meshCenter);

              // Transform to world coordinates
              const worldCenter = meshCenter.clone().applyMatrix4(mesh.matrixWorld);

              // Extract X and Z coordinates (Y is up) for sprite positioning
              localPos = new THREE.Vector2(worldCenter.x, worldCenter.z);
            }
          }
        }
      }

      // Fallback: if we couldn't find the mesh, calculate from perimeter locations
      if (!localPos) {
        if (beacon.perimeter && beacon.perimeter.locations && Array.isArray(beacon.perimeter.locations) && beacon.perimeter.locations.length >= 3) {
          // Create a temporary mesh exactly like createRoomMesh to get the bounding box center
          const points3D: THREE.Vector2[] = [];
          beacon.perimeter.locations.forEach((loc: number[]) => {
            if (Array.isArray(loc) && loc.length >= 2) {
              const [lat, lon] = loc;
              const point = this.latLonToLocalVector2(lon, lat, centerLon, centerLat, 1);
              points3D.push(point);
            }
          });

          if (points3D.length < 3) {
            return; // Skip if not enough valid points
          }

          // Create shape with same mirror transformation as createRoomMesh
          const shape = new THREE.Shape();
          points3D.forEach((point: THREE.Vector2, index: number) => {
            if (index === 0) {
              shape.moveTo(-point.x, -point.y);
            } else {
              shape.lineTo(-point.x, -point.y);
            }
          });

          // Create geometry with same settings as createRoomMesh
          const extrudeSettings = { depth: 3.5, bevelEnabled: false };
          const tempGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
          tempGeometry.translate(0, 0, -1.75);
          tempGeometry.rotateX(-Math.PI / 2);

          // Get bounding box center (this is where the mesh center actually is)
          tempGeometry.computeBoundingBox();
          const bbox = tempGeometry.boundingBox;

          if (bbox) {
            // Get center in X-Z plane (Y is up after rotation)
            const centerX = (bbox.min.x + bbox.max.x) * 0.5;
            const centerZ = (bbox.min.z + bbox.max.z) * 0.5;
            localPos = new THREE.Vector2(centerX, centerZ);
          }

          // Clean up
          tempGeometry.dispose();
        }
      }

      // If still no position, use beacon's actual GPS coordinates
      if (!localPos) {
        if (!beacon.position || !beacon.position.coordinates ||
          !Array.isArray(beacon.position.coordinates) ||
          beacon.position.coordinates.length < 2) {
          return; // Skip invalid beacons
        }
        const [lon, lat] = beacon.position.coordinates; // Coordinates are [lon, lat]
        // Convert lat/lon to local 3D coordinates
        const localCoord = this.latLonToLocalVector2(lon, lat, centerLon, centerLat, 1);
        // Apply same mirror transformation to match room mesh coordinate space
        localPos = new THREE.Vector2(-localCoord.x, -localCoord.y);
      }

      // Create sprite with Bluetooth icon - use fixed size for all beacons
      const spriteMaterial = new THREE.SpriteMaterial({
        map: bluetoothTexture, // Share the same texture for all beacons
        transparent: true,
        alphaTest: 0.1,
        depthTest: false,
        depthWrite: false,
        sizeAttenuation: false // Keep sprite size constant in screen space (pixels), not world space
      });

      const sprite = new THREE.Sprite(spriteMaterial);

      // Use pre-calculated scale (same for all beacons) - ensures all beacons have exactly the same size on screen
      sprite.scale.set(spriteScale, spriteScale, 1);

      // Position beacon clearly above the floor surface
      // Place beacon well above the mesh at y = 6 to ensure it's clearly visible
      const beaconPos = localPos;
      const beaconRoomId = beacon.perimeter?.id;
      const beaconHeight = this.resolveBadgeHeight(beaconPos, beaconRoomId);
      sprite.position.set(
        beaconPos.x,
        beaconHeight,
        beaconPos.y
      );

      // Ensure sprites are always visible during rotation
      // Set high renderOrder so sprites render after meshes
      sprite.renderOrder = 999;
      // Disable frustum culling to prevent icons from disappearing when rotating
      sprite.frustumCulled = false;

      // Sprites automatically face the camera

      // Store beacon data
      sprite.userData['isBeacon'] = true;
      sprite.userData['beaconId'] = beacon.id;
      sprite.userData['beaconLabel'] = beacon.label || beacon.name || '';
      if (beacon.perimeter) {
        sprite.userData['perimeterId'] = beacon.perimeter.id;
        sprite.userData['perimeterName'] = beacon.perimeter.name || beacon.perimeter.label || '';
      }

      scene.add(sprite);
    });

    // Refresh user badges after beacons are rendered
    // Use a small delay to ensure all beacons are added to the scene
    setTimeout(() => {
      this.refreshUserBadgesAfterBeaconRender();
    }, 50);
  }

  private refreshUserBadgesAfterBeaconRender(): void {
    // Check required dependencies
    if (!this.floorMapEntity || !this.threeScene || !this.threeCamera ||
      !this.threeRenderer || !this.floorMap || !this.clusterMarkers) {
      return;
    }

    // Try to use stored clustering data first (preferred - has full structure)
    let userData: any = null;
    let statuses: string[] = [];

    const storedData = this.lastUserDataForClustering;
    if (storedData && storedData.data) {
      // Use stored clustering data
      userData = storedData.data;
      statuses = storedData.statuses ?? [];
    }

    // Render users only if we have data
    if (userData) {
      this.renderUsersInThreeJs(userData, statuses);
    }
  }

  /**
   * Render users in Three.js scene with count numbers
   * Places user count badges at their positions showing the number of users
   * Uses the same coordinate transformation as room meshes to ensure alignment with Leaflet markers
   */
  private renderUsersInThreeJs(data: any, statuses?: string[]): void {
    if (!this.floorMapEntity || !this.threeScene || !this.threeCamera || !this.floorMap || !this.clusterMarkers) {
      return;
    }

    // Clear existing user sprites first (this also resets room colors)
    this.clearUsersInThreeJs();

    const scene = this.threeScene;
    // Use floor center as reference point (same as room meshes)
    const centerLat = this.floorMapEntity.centerpoint?.latitude || 0;
    const centerLon = this.floorMapEntity.centerpoint?.longitude || 0;

    // Get current map bounds and center for accurate positioning
    const mapCenter = this.floorMap.getCenter();
    const mapBounds = this.floorMap.getBounds();
    const currentZoom = this.floorMap.getZoom();

    // Calculate sprite scale for consistent sizing
    let badgeScale = this.USER_COUNT_BADGE_SIZE;
    if (this.threeCamera instanceof THREE.OrthographicCamera && this.threeRenderer) {
      const cameraHeight = this.threeCamera.top - this.threeCamera.bottom;
      const rendererHeight = this.threeRenderer.domElement.height;
      badgeScale = (this.USER_COUNT_BADGE_SIZE / rendererHeight) * cameraHeight;
    } else if (this.threeCamera instanceof THREE.PerspectiveCamera && this.threeRenderer) {
      badgeScale = this.USER_COUNT_BADGE_SIZE * 0.01;
    }

    // Check if data is clustered
    const dataIsCluster = (data as any).dataIsCluster;

    // Collect all user positions and counts for clustering
    const userBadges: Array<{ lat: number, lon: number, count: number, data: any }> = [];
    // Track which room IDs contain users
    const roomsWithUsers = new Set<string | number>();
    // Track which room IDs contain danger users (for red highlighting)
    const roomsWithDangerUsers = new Set<string | number>();
    // Track which room IDs contain only safe users (no danger, no unknown) - don't highlight these
    const roomsWithOnlySafeUsers = new Set<string | number>();
    // Track danger user counts per room
    const roomDangerCounts = new Map<string | number, number>();
    // Track room-level aggregates to determine if room is only safe
    const roomSafeCounts = new Map<string | number, number>();
    const roomUnknownCounts = new Map<string | number, number>();

    if (dataIsCluster && (data as any).rows) {
      // Handle clustered data - show total count for each cluster
      (data as any).rows.forEach((d: any) => {
        if (!d.position || !d.position.coordinates || d.position.coordinates.length < 2) {
          return;
        }

        const lat = d.position.coordinates[1];
        const lon = d.position.coordinates[0];
        const numDanger = d.danger ? parseInt(d.danger, 10) : 0;
        const numSafe = d.safe ? parseInt(d.safe, 10) : 0;
        const numUnknown = d.unknown ? parseInt(d.unknown, 10) : 0;
        const total = numDanger + numSafe + numUnknown;

        if (total === 0) {
          return; // Skip clusters with no users
        }

        // Find which room this user cluster is in
        const roomId = this.findRoomIdForUser(lat, lon);

        if (roomId !== null) {
          roomsWithUsers.add(roomId);
          // Track danger users separately
          if (numDanger > 0) {
            roomsWithDangerUsers.add(roomId);
            // Update danger count for this room
            const currentCount = roomDangerCounts.get(roomId) || 0;
            roomDangerCounts.set(roomId, currentCount + numDanger);
          }
          // Track room-level aggregates
          const currentSafeCount = roomSafeCounts.get(roomId) || 0;
          roomSafeCounts.set(roomId, currentSafeCount + numSafe);
          const currentUnknownCount = roomUnknownCounts.get(roomId) || 0;
          roomUnknownCounts.set(roomId, currentUnknownCount + numUnknown);
        }

        // Collect badge data for clustering
        userBadges.push({
          lat,
          lon,
          count: total,
          data: { ...d, numDanger, numSafe, numUnknown }
        });
      });
    } else if (Array.isArray(data)) {
      // Handle individual user data - show count of 1 or group by position
      const userGroups = new Map<string, any[]>();

      // Group users by position (rounded to avoid floating point issues)
      data.forEach((d: any) => {
        if (!d.position || !d.position.coordinates || d.position.coordinates.length < 2) {
          return;
        }
        const lat = d.position.coordinates[1];
        const lon = d.position.coordinates[0];
        // Round to 6 decimal places (~0.1m precision) to group nearby users
        const key = `${Math.round(lat * 1000000)},${Math.round(lon * 1000000)}`;
        if (!userGroups.has(key)) {
          userGroups.set(key, []);
        }
        userGroups.get(key)!.push(d);
      });

      // Collect badge data for clustering
      userGroups.forEach((users, key) => {
        const firstUser = users[0];
        const lat = firstUser.position.coordinates[1];
        const lon = firstUser.position.coordinates[0];
        const count = users.length;

        // Find which room this user group is in
        const roomId = this.findRoomIdForUser(lat, lon);

        if (roomId !== null) {
          roomsWithUsers.add(roomId);
          // Count danger users in this group
          const dangerCount = users.filter((u: any) =>
            u.status?.toUpperCase() === 'DANGER' || u.status?.toUpperCase() === 'UNSAFE'
          ).length;
          if (dangerCount > 0) {
            roomsWithDangerUsers.add(roomId);
            // Update danger count for this room
            const currentCount = roomDangerCounts.get(roomId) || 0;
            roomDangerCounts.set(roomId, currentCount + dangerCount);
          }
          // Count safe and unknown users
          const safeCount = users.filter((u: any) =>
            u.status?.toUpperCase() === 'SAFE'
          ).length;
          const unknownCount = users.filter((u: any) =>
            u.status?.toUpperCase() === 'UNKNOWN' || !u.status
          ).length;
          // Track room-level aggregates
          const currentSafeCount = roomSafeCounts.get(roomId) || 0;
          roomSafeCounts.set(roomId, currentSafeCount + safeCount);
          const currentUnknownCount = roomUnknownCounts.get(roomId) || 0;
          roomUnknownCounts.set(roomId, currentUnknownCount + unknownCount);
        }

        userBadges.push({
          lat,
          lon,
          count,
          data: { users, isIndividual: true }
        });
      });
    }

    // Determine which rooms have only safe users (no danger, no unknown) based on aggregated totals
    roomsWithUsers.forEach((roomId) => {
      const totalDanger = roomDangerCounts.get(roomId) || 0;
      const totalSafe = roomSafeCounts.get(roomId) || 0;
      const totalUnknown = roomUnknownCounts.get(roomId) || 0;

      // Room has only safe users if it has safe users but no danger and no unknown
      if (totalSafe > 0 && totalDanger === 0 && totalUnknown === 0) {
        roomsWithOnlySafeUsers.add(roomId);
      }
    });

    // Highlight rooms that contain users - red for danger, yellow for others (but not for only safe)
    if (roomsWithUsers.size > 0) {
      this.highlightRoomsWithUsers(Array.from(roomsWithUsers), Array.from(roomsWithDangerUsers), Array.from(roomsWithOnlySafeUsers), roomDangerCounts);
    } else {
      // Reset room colors if no users
      this.resetAllRoomColors();
    }

    // Apply clustering based on zoom level
    const clusteredBadges = this.clusterUserBadges(userBadges, currentZoom, centerLat, centerLon);

    // Render clustered badges
    clusteredBadges.forEach((cluster) => {
      // Check if this cluster contains danger users
      let hasDangerUsers = false;
      let dangerCount = 0;
      let hasSafeUsers = false;
      let safeCount = 0;
      let hasUnknownUsers = false;
      let unknownCount = 0;

      if (cluster.badges && cluster.badges.length > 0) {
        cluster.badges.forEach((badge: any) => {
          if (badge.data) {
            // Check clustered data
            if (badge.data.numDanger !== undefined && badge.data.numDanger > 0) {
              hasDangerUsers = true;
              dangerCount += badge.data.numDanger;
            }
            if (badge.data.numSafe !== undefined && badge.data.numSafe > 0) {
              hasSafeUsers = true;
              safeCount += badge.data.numSafe;
            }
            if (badge.data.numUnknown !== undefined && badge.data.numUnknown > 0) {
              hasUnknownUsers = true;
              unknownCount += badge.data.numUnknown;
            }

            // Check individual user data
            if (badge.data.users && Array.isArray(badge.data.users)) {
              const dangerUsers = badge.data.users.filter((u: any) =>
                u.status?.toUpperCase() === 'DANGER' || u.status?.toUpperCase() === 'UNSAFE'
              );
              if (dangerUsers.length > 0) {
                hasDangerUsers = true;
                dangerCount += dangerUsers.length;
              }

              const safeUsers = badge.data.users.filter((u: any) =>
                u.status?.toUpperCase() === 'SAFE'
              );
              if (safeUsers.length > 0) {
                hasSafeUsers = true;
                safeCount += safeUsers.length;
              }

              const unknownUsers = badge.data.users.filter((u: any) =>
                u.status?.toUpperCase() === 'UNKNOWN' || !u.status
              );
              if (unknownUsers.length > 0) {
                hasUnknownUsers = true;
                unknownCount += unknownUsers.length;
              }
            }
          }
        });
      }

      // Determine badge color:
      // - Red if has danger users (priority)
      // - Green if only safe users (no danger, no unknown)
      // - Yellow for mixed or unknown
      const isOnlySafe = hasSafeUsers && !hasDangerUsers && !hasUnknownUsers;

      // Find which room block this cluster is in and position badge at room center
      let badgePos: THREE.Vector2 | null = null;
      let roomId: string | number | null = null;

      // For danger users, always try to find the room center first
      if (hasDangerUsers) {
        roomId = this.findRoomIdForUser(cluster.lat, cluster.lon);
        if (roomId !== null) {
          // Get the exact room center for danger rooms
          badgePos = this.getRoomCenterPosition(roomId, centerLat, centerLon);
        }
      }

      // If room center not found, try the standard method
      if (!badgePos) {
        badgePos = this.findRoomCenterForUser(cluster.lat, cluster.lon, centerLat, centerLon);
        if (badgePos) {
          // Get room ID for this position
          roomId = this.findRoomIdForUser(cluster.lat, cluster.lon);
        }
      }

      // If still no room found, use the cluster's lat/lon position
      if (!badgePos) {
        const localPos = this.latLonToLocalVector2(cluster.lon, cluster.lat, centerLon, centerLat, 1);
        badgePos = new THREE.Vector2(-localPos.x, -localPos.y);
      }

      // Create count badge texture with segmented colors based on user status proportions
      const countTexture = this.createSegmentedUserCountTexture(
        cluster.totalCount,
        dangerCount,
        safeCount,
        unknownCount,
        hasDangerUsers,
        isOnlySafe
      );
      const spriteMaterial = new THREE.SpriteMaterial({
        map: countTexture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        sizeAttenuation: false
      });

      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.scale.set(badgeScale, badgeScale, 1);
      const badgeHeight = this.resolveBadgeHeight(badgePos, roomId);
      sprite.position.set(
        badgePos.x,
        badgeHeight,
        badgePos.y
      );
      sprite.renderOrder = 998; // Just below beacons
      sprite.frustumCulled = false;
      sprite.userData['isUser'] = true;
      sprite.userData['isUserCount'] = true;
      sprite.userData['isCluster'] = cluster.badgeCount > 1;
      sprite.userData['count'] = cluster.totalCount;
      sprite.userData['badgeCount'] = cluster.badgeCount;
      sprite.userData['clusterData'] = cluster.badges;
      sprite.userData['statuses'] = statuses;
      sprite.userData['hasDangerUsers'] = hasDangerUsers;
      sprite.userData['dangerCount'] = dangerCount;
      sprite.userData['hasSafeUsers'] = hasSafeUsers;
      sprite.userData['safeCount'] = safeCount;
      sprite.userData['isOnlySafe'] = isOnlySafe;
      sprite.userData['roomId'] = roomId;

      scene.add(sprite);
    });
  }

  private resolveBadgeHeight(
    badgePos: THREE.Vector2,
    roomId: string | number | null
  ): number {
    const EPS = 1e-6;

    // Origin case
    if (
      Math.abs(badgePos.x) < EPS &&
      Math.abs(badgePos.y) < EPS
    ) {
      return 0.25; // small lift above floor
    }

    // Room-based height
    if (roomId !== null) {
      return this.getBadgeHeightAboveRoom(roomId);
    }

    // Absolute fallback
    return 0.5;
  }

  private getBadgeHeightAboveRoom(roomId: string | number): number {
    if (!this.threeScene) {
      return 0;
    }

    let roomObject: THREE.Object3D | null = null;

    this.threeScene.traverse((obj: THREE.Object3D) => {
      if (obj.userData && obj.userData['roomId'] === roomId) {
        roomObject = obj;
      }
    });

    if (!roomObject) {
      return 0;
    }

    const box = new THREE.Box3().setFromObject(roomObject);

    const size = new THREE.Vector3();
    box.getSize(size);

    const center = new THREE.Vector3();
    box.getCenter(center);

    // Slightly above room top (relative offset)
    const offset = size.y * 0.15;

    return center.y + size.y / 2 + offset;
  }

  /**
   * Cluster user badges based on zoom level
   * When zoomed out, nearby badges are combined into a single badge showing total count
   */
  private clusterUserBadges(
    badges: Array<{ lat: number; lon: number; count: number; data: any }>,
    zoom: number,
    centerLat: number,
    centerLon: number
  ): Array<{
    lat: number;
    lon: number;
    totalCount: number;
    badgeCount: number;
    badges: any[];
  }> {
    if (!badges || badges.length === 0) {
      return [];
    }

    const isAlreadyAggregated = badges.every(
      b => Array.isArray(b.data?.users)
    );

    const CLUSTER_ZOOM_THRESHOLD = 16;

    // Zoomed IN → show individual rooms
    if (isAlreadyAggregated && zoom >= CLUSTER_ZOOM_THRESHOLD) {
      return badges.map(badge => ({
        lat: badge.lat,
        lon: badge.lon,
        totalCount: badge.count,
        badgeCount: 1,
        badges: [badge]
      }));
    }

    // Zoomed OUT → allow clustering (merge totals)
    let clusterThreshold: number;
    if (zoom < 14) {
      clusterThreshold = 200;
    } else if (zoom < 16) {
      clusterThreshold = 100;
    } else if (zoom < 18) {
      clusterThreshold = 50;
    } else {
      clusterThreshold = 20;
    }

    const avgLat =
      badges.reduce((sum, b) => sum + b.lat, 0) / badges.length || centerLat;

    const latRad = (avgLat * Math.PI) / 180;
    const metersPerDegreeLat = 111000;
    const metersPerDegreeLon = 111000 * Math.cos(latRad);

    const thresholdDegreesLat = clusterThreshold / metersPerDegreeLat;
    const thresholdDegreesLon = clusterThreshold / metersPerDegreeLon;

    const clusters: any[] = [];
    const processed = new Set<number>();

    badges.forEach((badge, index) => {
      if (processed.has(index)) return;

      const cluster = {
        lat: badge.lat,
        lon: badge.lon,
        totalCount: badge.count,
        badgeCount: 1,
        badges: [badge]
      };

      processed.add(index);

      badges.forEach((otherBadge, otherIndex) => {
        if (index === otherIndex || processed.has(otherIndex)) return;

        const latDiff = Math.abs(badge.lat - otherBadge.lat);
        const lonDiff = Math.abs(badge.lon - otherBadge.lon);

        if (
          latDiff <= thresholdDegreesLat &&
          lonDiff <= thresholdDegreesLon
        ) {
          cluster.totalCount += otherBadge.count;
          cluster.badgeCount++;
          cluster.badges.push(otherBadge);
          processed.add(otherIndex);
        }
      });

      clusters.push(cluster);
    });

    return clusters;
  }

  private createSegmentedUserCountTexture(
    totalCount: number,
    dangerCount: number,
    safeCount: number,
    unknownCount: number,
    hasDangerUsers: boolean = false,
    isOnlySafe: boolean = false
  ): THREE.Texture {

    const SIZE = 256;
    const CENTER = SIZE / 2;
    const OUTER_RADIUS = CENTER - 8;
    const INNER_RADIUS = CENTER - 40;

    // Colors
    const COLORS = {
      danger: '#d05d63',
      safe: '#0FFA1F',
      unknown: '#ffd900',
      innerDanger: '#ff9999',
      innerSafe: '#fff9c4',
      text: '#000000'
    };

    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, SIZE, SIZE);

    /* -------------------- Helpers -------------------- */

    const drawCircle = (radius: number, color: string) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(CENTER, CENTER, radius, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawText = () => {
      const text = String(totalCount);
      let fontSize = 96;
      if (text.length > 2) fontSize = 72;
      if (text.length > 3) fontSize = 56;

      ctx.fillStyle = COLORS.text;
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, CENTER, CENTER);
    };

    const createTexture = () => {
      const texture = new THREE.Texture(canvas);
      texture.needsUpdate = true;
      return texture;
    };

    /* -------------------- Ratios -------------------- */

    const total = dangerCount + safeCount + unknownCount;
    const dangerRatio = total ? dangerCount / total : 0;
    const safeRatio = total ? safeCount / total : 0;
    const unknownRatio = total ? unknownCount / total : 0;

    /* -------------------- Case 1: Only Safe -------------------- */

    if (isOnlySafe && !hasDangerUsers && unknownCount === 0) {
      drawCircle(OUTER_RADIUS, COLORS.safe);
      drawCircle(INNER_RADIUS, '#e2dfdfff'); // Grey inner circle for only safe users
      drawText();
      return createTexture();
    }

    /* -------------------- Case 2: Only Danger -------------------- */

    if (hasDangerUsers && safeCount === 0 && unknownCount === 0) {
      drawCircle(OUTER_RADIUS, COLORS.danger);
      drawCircle(INNER_RADIUS, COLORS.innerDanger);
      drawText();
      return createTexture();
    }

    /* -------------------- Case 3: Segmented Badge -------------------- */

    let currentAngle = -Math.PI / 2;

    const drawSegment = (ratio: number, color: string) => {
      if (ratio <= 0) return;
      const angle = ratio * Math.PI * 2;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(CENTER, CENTER);
      ctx.arc(CENTER, CENTER, OUTER_RADIUS, currentAngle, currentAngle + angle);
      ctx.closePath();
      ctx.fill();
      currentAngle += angle;
    };

    drawSegment(dangerRatio, COLORS.danger);
    drawSegment(safeRatio, COLORS.safe);
    drawSegment(unknownRatio, COLORS.unknown);

    // Inner background depends on danger presence
    drawCircle(
      INNER_RADIUS,
      hasDangerUsers ? COLORS.innerDanger : COLORS.innerSafe
    );

    drawText();
    return createTexture();
  }


  /**
   * Find the room center for a user based on their lat/lon position
   * Returns the center of the room block if user is inside a room, null otherwise
   */
  private findRoomCenterForUser(lat: number, lon: number, centerLat: number, centerLon: number): THREE.Vector2 | null {
    if (!this.floorMapEntity.perimeters || !this.threeScene) {
      return null;
    }

    const userPoint = [lat, lon];

    // Get all room perimeters (exclude floor perimeters)
    const allPerimeters = typeof this.floorMapEntity.perimeters === 'object'
      ? Object.values(this.floorMapEntity.perimeters)
      : Array.isArray(this.floorMapEntity.perimeters)
        ? this.floorMapEntity.perimeters
        : [];

    const roomPerimeters = allPerimeters.filter((p: any) => {
      return p && p.perimeterType !== 'FLOOR' && p.type !== 'FLOOR';
    });

    // Check each room perimeter to see if it contains the user
    for (const perimeter of roomPerimeters) {
      if (perimeter && perimeter.locations && Array.isArray(perimeter.locations) &&
        this.isPointInPolygon(userPoint, perimeter.locations)) {
        // User is in this room - find the room mesh and use its center
        let roomMesh: THREE.Object3D | null = null;
        this.threeScene.traverse((object) => {
          if (object.userData['isRoomMesh'] && object.userData['roomId'] === perimeter.id) {
            roomMesh = object;
          }
        });

        if (roomMesh) {
          const mesh = roomMesh as THREE.Mesh;
          if (mesh instanceof THREE.Mesh && mesh.geometry) {
            mesh.geometry.computeBoundingBox();
            const bbox = mesh.geometry.boundingBox;
            if (bbox) {
              const meshCenter = new THREE.Vector3();
              bbox.getCenter(meshCenter);
              const worldCenter = meshCenter.clone().applyMatrix4(mesh.matrixWorld);
              return new THREE.Vector2(worldCenter.x, worldCenter.z);
            }
          }
        }

        // Fallback: calculate center from perimeter locations if mesh not found
        if (perimeter.locations && perimeter.locations.length >= 3) {
          const points3D: THREE.Vector2[] = [];
          perimeter.locations.forEach((loc: number[]) => {
            if (Array.isArray(loc) && loc.length >= 2) {
              const [locLat, locLon] = loc;
              const point = this.latLonToLocalVector2(locLon, locLat, centerLon, centerLat, 1);
              points3D.push(point);
            }
          });

          if (points3D.length >= 3) {
            const shape = new THREE.Shape();
            points3D.forEach((point: THREE.Vector2, index: number) => {
              if (index === 0) {
                shape.moveTo(-point.x, -point.y);
              } else {
                shape.lineTo(-point.x, -point.y);
              }
            });

            const extrudeSettings = { depth: 3.5, bevelEnabled: false };
            const tempGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
            tempGeometry.translate(0, 0, -1.75);
            tempGeometry.rotateX(-Math.PI / 2);
            tempGeometry.computeBoundingBox();
            const bbox = tempGeometry.boundingBox;

            if (bbox) {
              const centerX = (bbox.min.x + bbox.max.x) * 0.5;
              const centerZ = (bbox.min.z + bbox.max.z) * 0.5;
              tempGeometry.dispose();
              return new THREE.Vector2(centerX, centerZ);
            }

            tempGeometry.dispose();
          }
        }
      }
    }

    return null;
  }

  /**
   * Get the exact center position of a room by its ID
   * Returns the center position in 3D space (Vector2 with x, y coordinates)
   */
  private getRoomCenterPosition(roomId: string | number, centerLat: number, centerLon: number): THREE.Vector2 | null {
    if (!this.floorMapEntity.perimeters || !this.threeScene) {
      return null;
    }

    // Get all room perimeters (exclude floor perimeters)
    const allPerimeters = typeof this.floorMapEntity.perimeters === 'object'
      ? Object.values(this.floorMapEntity.perimeters)
      : Array.isArray(this.floorMapEntity.perimeters)
        ? this.floorMapEntity.perimeters
        : [];

    const roomPerimeters = allPerimeters.filter((p: any) => {
      return p && p.perimeterType !== 'FLOOR' && p.type !== 'FLOOR';
    });

    // Find the perimeter with matching ID
    const perimeter = roomPerimeters.find((p: any) =>
      p && (p.id === roomId || p.perimeterId === roomId)
    );

    if (!perimeter || !perimeter.locations || !Array.isArray(perimeter.locations)) {
      return null;
    }

    // First, try to find the room mesh in the scene and use its bounding box center
    let roomMesh: THREE.Object3D | null = null;
    this.threeScene.traverse((object) => {
      if (object.userData['isRoomMesh'] && object.userData['roomId'] === roomId) {
        roomMesh = object;
      }
    });

    if (roomMesh) {
      const mesh = roomMesh as THREE.Mesh;
      if (mesh instanceof THREE.Mesh && mesh.geometry) {
        mesh.geometry.computeBoundingBox();
        const bbox = mesh.geometry.boundingBox;
        if (bbox) {
          const meshCenter = new THREE.Vector3();
          bbox.getCenter(meshCenter);
          const worldCenter = meshCenter.clone().applyMatrix4(mesh.matrixWorld);
          return new THREE.Vector2(worldCenter.x, worldCenter.z);
        }
      }
    }

    // Fallback: calculate center from perimeter locations
    if (perimeter.locations && perimeter.locations.length >= 3) {
      const points3D: THREE.Vector2[] = [];
      perimeter.locations.forEach((loc: number[]) => {
        if (Array.isArray(loc) && loc.length >= 2) {
          const [locLat, locLon] = loc;
          const point = this.latLonToLocalVector2(locLon, locLat, centerLon, centerLat, 1);
          points3D.push(point);
        }
      });

      if (points3D.length >= 3) {
        const shape = new THREE.Shape();
        points3D.forEach((point: THREE.Vector2, index: number) => {
          if (index === 0) {
            shape.moveTo(-point.x, -point.y);
          } else {
            shape.lineTo(-point.x, -point.y);
          }
        });

        const extrudeSettings = { depth: 3.5, bevelEnabled: false };
        const tempGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        tempGeometry.translate(0, 0, -1.75);
        tempGeometry.rotateX(-Math.PI / 2);
        tempGeometry.computeBoundingBox();
        const bbox = tempGeometry.boundingBox;

        if (bbox) {
          const centerX = (bbox.min.x + bbox.max.x) * 0.5;
          const centerZ = (bbox.min.z + bbox.max.z) * 0.5;
          tempGeometry.dispose();
          return new THREE.Vector2(centerX, centerZ);
        }

        tempGeometry.dispose();
      }
    }

    return null;
  }

  /**
   * Check if a point is inside a polygon using ray casting algorithm
   * Point format: [lat, lon]
   * Polygon format: array of [lat, lon] points
   */
  private isPointInPolygon(point: number[], polygon: number[][]): boolean {
    if (!polygon || polygon.length < 3) {
      return false;
    }

    const [lat, lon] = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [latI, lonI] = polygon[i];
      const [latJ, lonJ] = polygon[j];

      const intersect = ((latI > lat) !== (latJ > lat)) &&
        (lon < (lonJ - lonI) * (lat - latI) / (latJ - latI) + lonI);

      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  }

  /**
   * Find the room ID for a user based on their lat/lon position
   * Returns the room ID if user is inside a room, null otherwise
   */
  private findRoomIdForUser(lat: number, lon: number): string | number | null {
    if (!this.floorMapEntity.perimeters) {
      return null;
    }

    const userPoint = [lat, lon];

    // Get all room perimeters (exclude floor perimeters)
    const allPerimeters = typeof this.floorMapEntity.perimeters === 'object'
      ? Object.values(this.floorMapEntity.perimeters)
      : Array.isArray(this.floorMapEntity.perimeters)
        ? this.floorMapEntity.perimeters
        : [];

    const roomPerimeters = allPerimeters.filter((p: any) => {
      return p && p.perimeterType !== 'FLOOR' && p.type !== 'FLOOR';
    });

    // Check each room perimeter to see if it contains the user
    for (const perimeter of roomPerimeters) {
      if (perimeter && perimeter.locations && Array.isArray(perimeter.locations) &&
        this.isPointInPolygon(userPoint, perimeter.locations)) {
        // User is in this room - return the room ID
        return perimeter.id || perimeter.perimeterId || null;
      }
    }

    return null;
  }

  /**
   * Highlight rooms that contain users by changing their color
   * Red for rooms with danger users, yellow for rooms with other users (but not for only safe)
   * @param roomIds Array of room IDs to highlight
   * @param dangerRoomIds Array of room IDs that contain danger users (will be highlighted in red)
   * @param onlySafeRoomIds Array of room IDs that contain only safe users (will NOT be highlighted)
   * @param roomDangerCounts Map of room IDs to danger user counts (for displaying count)
   */
  private highlightRoomsWithUsers(
    roomIds: (string | number)[],
    dangerRoomIds: (string | number)[] = [],
    onlySafeRoomIds: (string | number)[] = [],
    roomDangerCounts: Map<string | number, number> = new Map()
  ): void {
    if (!this.threeScene || roomIds.length === 0) {
      return;
    }

    // Red color for rooms with danger users
    const redColor = new THREE.Color(0xff6666); // Red for danger

    // Yellow color for rooms with other users (safe/unknown, but not only safe)
    const yellowColor = new THREE.Color(0xf6c543); // Same yellow as user badges

    this.threeScene.traverse((object) => {
      if (object.userData['isRoomMesh'] && object.userData['roomId'] !== undefined) {
        const roomId = object.userData['roomId'];
        if (roomIds.includes(roomId)) {
          // Skip highlighting if room has only safe users
          if (onlySafeRoomIds.includes(roomId)) {
            return; // Don't highlight rooms with only safe users
          }

          // This room has users - highlight based on danger status
          if (object instanceof THREE.Mesh && object.material instanceof THREE.MeshPhongMaterial) {
            // Priority: Red for danger users, yellow for others
            if (dangerRoomIds.includes(roomId)) {
              // Room has danger users - highlight in red
              object.material.color.copy(redColor);
              // Store danger count in userData for potential display
              const dangerCount = roomDangerCounts.get(roomId) || 0;
              object.userData['dangerCount'] = dangerCount;
            } else {
              // Room has users but no danger - highlight in yellow
              object.material.color.copy(yellowColor);
            }
            // Ensure material is marked as needing update
            object.material.needsUpdate = true;
          }
        }
      }
    });
  }

  /**
   * Reset all room colors to their original colors
   * Called before highlighting rooms with users to ensure clean state
   */
  private resetAllRoomColors(): void {
    if (!this.threeScene) {
      return;
    }

    this.threeScene.traverse((object) => {
      if (object.userData['isRoomMesh'] && object.userData['originalColor']) {
        // Restore original color
        if (object instanceof THREE.Mesh && object.material instanceof THREE.MeshPhongMaterial) {
          const originalColor = object.userData['originalColor'] as THREE.Color;
          object.material.color.copy(originalColor);
          // Ensure material is marked as needing update
          object.material.needsUpdate = true;
        }
      }
    });
  }


  /**
   * Hide Leaflet cluster markers visually using CSS
   * Keeps markers in data structure for click handlers and functionality
   */
  private hideLeafletClusterMarkersVisually(): void {
    if (!this.floorMap) {
      return;
    }

    const mapContainer = this.floorMap.getContainer();
    if (!mapContainer) {
      return;
    }

    // Hide all marker cluster elements visually
    const clusterElements = mapContainer.querySelectorAll('.marker-cluster, .leaflet-marker-icon');
    clusterElements.forEach((el: any) => {
      if (el && el.style) {
        // Hide visually but keep in DOM for functionality
        el.style.display = 'none';
        el.style.visibility = 'hidden';
        el.style.opacity = '0';
        // Keep pointer events disabled so they don't interfere
        el.style.pointerEvents = 'none';
      }
    });

    // Also hide any cluster group containers
    const clusterGroups = mapContainer.querySelectorAll('.leaflet-marker-pane');
    clusterGroups.forEach((pane: any) => {
      if (pane && pane.style) {
        const markers = pane.querySelectorAll('.marker-cluster, .leaflet-marker-icon');
        markers.forEach((marker: any) => {
          if (marker && marker.style) {
            marker.style.display = 'none';
            marker.style.visibility = 'hidden';
            marker.style.opacity = '0';
            marker.style.pointerEvents = 'none';
          }
        });
      }
    });
  }

  /**
   * Clear all user sprites from Three.js scene
   */
  private clearUsersInThreeJs(): void {
    if (!this.threeScene) {
      return;
    }

    const usersToRemove: THREE.Object3D[] = [];
    this.threeScene.traverse((object) => {
      if (object.userData['isUser'] || object.userData['isUserCount']) {
        usersToRemove.push(object);
      }
    });

    usersToRemove.forEach(obj => {
      if (obj instanceof THREE.Sprite) {
        if (obj.material instanceof THREE.SpriteMaterial && obj.material.map) {
          obj.material.map.dispose();
        }
        if (obj.material) {
          obj.material.dispose();
        }
      }
      this.threeScene!.remove(obj);
    });

    // Reset all room colors when users are cleared
    this.resetAllRoomColors();
  }

  /**
   * Create a floor mesh from coordinates
   */
  private createFloorMesh(locations: number[][], centerLat: number, centerLon: number): THREE.Mesh | null {
    if (!locations || locations.length < 3) {
      return null;
    }

    const shape = new THREE.Shape();
    const points: THREE.Vector2[] = [];

    locations.forEach((loc: number[], index: number) => {
      if (Array.isArray(loc) && loc.length >= 2) {
        const [lat, lon] = loc;
        const point = this.latLonToLocalVector2(lon, lat, centerLon, centerLat, 1);
        points.push(point);
        // MIRROR FIX: Flip both X and Y coordinates (horizontal + vertical mirror) to fix orientation
        // -point.x flips left-right (horizontal mirror)
        // -point.y flips up-down (vertical mirror) - niche wala upar, upar wala niche
        if (index === 0) {
          shape.moveTo(-point.x, -point.y);
        } else {
          shape.lineTo(-point.x, -point.y);
        }
      }
    });

    if (points.length < 3) {
      return null;
    }

    const extrudeSettings = { depth: 0.01, bevelEnabled: false };
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.rotateX(-Math.PI / 2);

    const material = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      opacity: 0.3,
      transparent: true,
      depthWrite: false
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = 0.02;
    return mesh;
  }

  /**
   * Create a room mesh from coordinates
   */
  private createRoomMesh(
    locations: number[][],
    centerLat: number,
    centerLon: number,
    roomId: string | number
  ): THREE.Mesh | null {

    if (!locations || locations.length < 3) {
      return null;
    }

    const shape = new THREE.Shape();
    const points: THREE.Vector2[] = [];

    locations.forEach((loc: number[], index: number) => {
      if (Array.isArray(loc) && loc.length >= 2) {
        const [lat, lon] = loc;
        const point = this.latLonToLocalVector2(
          lon,
          lat,
          centerLon,
          centerLat,
          1
        );

        points.push(point);

        // MIRROR FIX (keep your existing logic)
        if (index === 0) {
          shape.moveTo(-point.x, -point.y);
        } else {
          shape.lineTo(-point.x, -point.y);
        }
      }
    });

    if (points.length < 3) {
      return null;
    }

    /* ---------- Geometry ---------- */
    const extrudeSettings = { depth: 0.01, bevelEnabled: false };
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.rotateX(-Math.PI / 2);

    /* ---------- PERSISTENT COLOR ---------- */
    let originalColor = this.roomColorMap.get(roomId);

    // Generate color ONLY ON FIRST RENDER
    if (!originalColor) {
      const gray = Math.random() * 0.35 + 0.35;
      originalColor = new THREE.Color(gray, gray, gray);
      this.roomColorMap.set(roomId, originalColor);
    }

    /* ---------- Material ---------- */
    const material = new THREE.MeshPhongMaterial({
      color: originalColor.clone(),
      side: THREE.DoubleSide,
      opacity: 0.8,
      transparent: true,
      depthWrite: false
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = 0.04;

    /* ---------- Metadata ---------- */
    mesh.userData['originalColor'] = originalColor.clone();
    mesh.userData['roomId'] = roomId;
    mesh.userData['isRoomMesh'] = true;

    mesh.renderOrder = 0;

    /* ---------- Borders ---------- */
    const edges = new THREE.EdgesGeometry(geometry, 25);
    const borderMaterial = new THREE.LineBasicMaterial({
      color: 0x555555,
      linewidth: 1.5
    });

    const borderLines = new THREE.LineSegments(edges, borderMaterial);
    mesh.add(borderLines);

    return mesh;
  }

  /**
   * Convert lat/lon to local Vector2 coordinates
   * Returns offset from center point in meters
   * X = longitude offset (east-west), Y = latitude offset (north-south)
   */
  private latLonToLocalVector2(lon: number, lat: number, centerLon: number, centerLat: number, scale: number): THREE.Vector2 {
    const R = 6371000; // Earth's radius in meters
    const lat1Rad = (centerLat * Math.PI) / 180;
    const lat2Rad = (lat * Math.PI) / 180;
    const deltaLatRad = ((lat - centerLat) * Math.PI) / 180;
    const deltaLonRad = ((lon - centerLon) * Math.PI) / 180;
    const avgLatRad = (lat1Rad + lat2Rad) / 2;

    // Calculate offsets in meters
    // X = longitude offset (east = positive, west = negative)
    // Y = latitude offset (north = positive, south = negative)
    const deltaX = deltaLonRad * R * Math.cos(avgLatRad);
    const deltaY = deltaLatRad * R;

    return new THREE.Vector2(deltaX * scale, deltaY * scale);
  }

  /**
   * Intercept Leaflet zoom button clicks to control Three.js zoom
   */
  private interceptLeafletZoomButtons(): void {
    if (!this.floorMap) {
      return;
    }

    // Wait for Leaflet controls to be rendered
    setTimeout(() => {
      const mapContainer = this.floorMap!.getContainer();
      if (!mapContainer) {
        return;
      }

      // Find zoom in button (usually has class 'leaflet-control-zoom-in')
      const zoomInButton = mapContainer.querySelector('.leaflet-control-zoom-in');
      if (zoomInButton) {
        zoomInButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.zoomThreeJsFloor(true); // Zoom in
        });
      }

      // Find zoom out button (usually has class 'leaflet-control-zoom-out')
      const zoomOutButton = mapContainer.querySelector('.leaflet-control-zoom-out');
      if (zoomOutButton) {
        zoomOutButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.zoomThreeJsFloor(false); // Zoom out
        });
      }
    }, 200);
  }

  /**
   * Zoom Three.js floor in or out
   */
  private zoomThreeJsFloor(zoomIn: boolean): void {
    if (!this.threeCamera || !this.threeControls || !this.threeScene) {
      return;
    }

    // Calculate current distance from camera to target
    const target = this.threeControls.target.clone();
    const cameraPos = this.threeCamera.position.clone();
    const direction = new THREE.Vector3().subVectors(cameraPos, target).normalize();
    const currentDistance = cameraPos.distanceTo(target);

    // Calculate zoom factor (zoom in: move closer, zoom out: move farther)
    const zoomFactor = zoomIn ? 0.8 : 1.25;
    const newDistance = currentDistance * zoomFactor;

    // Get zoom limits
    const minDistance = this.threeControls.minDistance || 10;
    const maxDistance = this.threeControls.maxDistance || 5000;

    // Clamp to zoom limits
    const clampedDistance = Math.max(minDistance, Math.min(maxDistance, newDistance));

    // Calculate new camera position
    const directionScaled = direction.clone().multiplyScalar(clampedDistance);
    const newPosition = new THREE.Vector3().addVectors(target, directionScaled);

    // Update camera position
    this.threeCamera.position.copy(newPosition);
    this.threeControls.update();
  }

  /**
   * Fit camera to show the entire floor
   */
  private fitCameraToFloor(): void {
    if (
      this.cameraLocked ||
      !this.threeScene ||
      !this.threeCamera ||
      !this.threeControls
    ) {
      return;
    }

    let floorMesh: THREE.Object3D | null = null;

    this.threeScene.traverse(obj => {
      if (obj.userData?.['isFloorMesh']) {
        floorMesh = obj;
      }
    });

    if (!floorMesh) return;

    const box = new THREE.Box3().setFromObject(floorMesh);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    const maxDim = Math.max(size.x, size.z);
    if (maxDim <= 0) return;

    const distance = maxDim * 1.2;

    this.threeCamera.position.set(
      center.x,
      center.y + distance,
      center.z + distance
    );

    this.threeCamera.lookAt(center);
    this.threeControls.target.copy(center);
    this.threeControls.update();

    this.cameraLocked = true;
  }

  /**
   * Start the animation loop
   */
  private startAnimationLoop(): void {
    const animate = () => {
      if (this.threeScene && this.threeCamera && this.threeRenderer && this.threeControls) {
        this.threeControls.update();
        this.threeRenderer.render(this.threeScene, this.threeCamera);
      }
      this.animationFrameId = requestAnimationFrame(animate);
    };
    animate();
  }

  /**
   * Setup Leaflet event listeners to sync Three.js camera with Leaflet map
   */
  private setupLeafletSyncListeners(): void {
    if (!this.floorMap) {
      return;
    }

    // Use requestAnimationFrame to throttle sync updates for smooth performance
    let syncRequestId: number | null = null;
    const throttledSync = () => {
      // Always disable controls during sync to ensure floor moves with map
      if (this.threeControls) {
        this.threeControls.enabled = false;
      }

      if (syncRequestId !== null) {
        cancelAnimationFrame(syncRequestId);
      }
      syncRequestId = requestAnimationFrame(() => {
        // Always sync when map moves - floor should follow map
        if (this.floorMap && this.threeCamera && this.threeScene && this.threeControls) {
          this.syncThreeJsWithLeaflet();
        }
        syncRequestId = null;
      });
    };

    // Track when map dragging starts
    const dragStartHandler = () => {
      this.isMapDragging = true;
      if (this.threeControls) {
        this.threeControls.enabled = false;
      }
    };

    // Track when map dragging ends
    const dragEndHandler = () => {
      this.isMapDragging = false;
      // Final sync when drag ends
      this.syncThreeJsWithLeaflet();
    };

    // Sync on map move (pan) - use throttled version for smooth updates
    const moveHandler = () => {
      // Always disable controls during map movement so sync can work properly
      // This is critical for left/right movement to work correctly
      if (this.threeControls) {
        this.threeControls.enabled = false;
      }
      // Ensure sync happens - throttled for performance but responsive
      throttledSync();
    };

    // Sync on zoom - use throttled version for smooth updates
    const zoomHandler = () => {
      // Disable controls during zoom so sync can work properly
      if (this.threeControls) {
        this.threeControls.enabled = false;
      }
      throttledSync();
    };

    // Also sync on moveend and zoomend for final positioning
    const moveEndHandler = () => {
      // Always sync when map move ends - Three.js floor should move with map
      if (this.threeControls) {
        this.threeControls.enabled = false;
      }
      this.syncThreeJsWithLeaflet();
    };

    const zoomEndHandler = () => {
      // Always sync when zoom ends - Three.js floor should move with map
      if (this.threeControls) {
        this.threeControls.enabled = false;
      }
      this.syncThreeJsWithLeaflet();

      // Re-render user badges with updated clustering based on new zoom level
      if (this.lastUserDataForClustering && this.threeScene && this.threeJsInitialized) {
        // Use stored data to re-render with new clustering
        setTimeout(() => {
          this.renderUsersInThreeJs(
            this.lastUserDataForClustering.data,
            this.lastUserDataForClustering.statuses
          );
        }, 100);
      }
    };

    // Listen to all relevant events
    this.floorMap.on('movestart', dragStartHandler);
    this.floorMap.on('move', moveHandler);
    this.floorMap.on('moveend', moveEndHandler);
    this.floorMap.on('dragstart', dragStartHandler);
    this.floorMap.on('dragend', dragEndHandler);
    this.floorMap.on('zoom', zoomHandler);
    this.floorMap.on('zoomend', zoomEndHandler);
    this.floorMap.on('viewreset', moveEndHandler); // Also sync on view reset

    // Store handlers for cleanup
    this.leafletSyncListeners['movestart'] = dragStartHandler;
    this.leafletSyncListeners['move'] = moveHandler;
    this.leafletSyncListeners['moveend'] = moveEndHandler;
    this.leafletSyncListeners['dragstart'] = dragStartHandler;
    this.leafletSyncListeners['dragend'] = dragEndHandler;
    this.leafletSyncListeners['zoom'] = zoomHandler;
    this.leafletSyncListeners['zoomend'] = zoomEndHandler;
    this.leafletSyncListeners['viewreset'] = moveEndHandler;
  }

  /**
   * Remove Leaflet sync event listeners
   */
  private removeLeafletSyncListeners(): void {
    if (!this.floorMap) {
      return;
    }

    Object.keys(this.leafletSyncListeners).forEach((event) => {
      const handler = this.leafletSyncListeners[event];
      this.floorMap!.off(event as any, handler);
    });

    this.leafletSyncListeners = {};
  }

  /**
   * Sync Three.js camera with Leaflet map view
   * This keeps the Three.js floor aligned with the Leaflet map
   */
  private syncThreeJsWithLeaflet(): void {
    if (!this.floorMap || !this.threeCamera || !this.threeScene || !this.threeControls) {
      return;
    }


    try {
      // CRITICAL: Disable controls during sync to ensure camera position is set correctly
      // Without this, controls can interfere with camera positioning, especially for X-axis movement
      this.threeControls.enabled = false;

      // Get Leaflet map center, zoom, and bounds
      const center = this.floorMap.getCenter();
      const zoom = this.floorMap.getZoom();
      const bounds = this.floorMap.getBounds();

      // Get floor center point (reference point for 3D coordinates)
      const floorCenterLat = this.floorMapEntity.centerpoint?.latitude || center.lat;
      const floorCenterLon = this.floorMapEntity.centerpoint?.longitude || center.lng;

      // Calculate the visible bounds in meters
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();

      // Convert bounds corners to local 3D coordinates
      const neOffset = this.latLonToLocalVector2(
        northEast.lng,
        northEast.lat,
        floorCenterLon,
        floorCenterLat,
        1
      );
      const swOffset = this.latLonToLocalVector2(
        southWest.lng,
        southWest.lat,
        floorCenterLon,
        floorCenterLat,
        1
      );

      // Calculate the visible area size in 3D space
      const visibleWidth = Math.abs(neOffset.x - swOffset.x);
      const visibleHeight = Math.abs(neOffset.y - swOffset.y);

      // Calculate center offset from floor center to current map center
      // This makes the floor move with the map - when map center changes, camera follows
      const centerOffset = this.latLonToLocalVector2(
        center.lng,
        center.lat,
        floorCenterLon,
        floorCenterLat,
        1
      );

      // For OrthographicCamera, set the camera size to match visible area
      if (this.threeCamera instanceof THREE.OrthographicCamera) {
        const container = this.floorMapElement.nativeElement;
        // Calculate orthographic camera size to match Leaflet visible bounds exactly
        // visibleWidth and visibleHeight are in meters
        const sizeX = visibleWidth * 0.5; // Half-width
        const sizeY = visibleHeight * 0.5; // Half-height

        // Position camera looking straight down at the current map center
        // This makes the floor move with the map - when map moves, camera follows map center
        // Floor is in X-Z plane (Y is up), so camera looks down along -Y axis
        // centerOffset.x = longitude offset (east-west), centerOffset.y = latitude offset (north-south)

        // CRITICAL: For orthographic camera to sync with Leaflet map movement
        // The camera must be positioned and oriented correctly for both X (longitude) and Z (latitude) axes

        // CRITICAL FIX: Ensure proper camera setup for orthographic sync with Leaflet
        // The order of operations is critical for correct coordinate system alignment

        // Step 1: Set up vector FIRST - must be before position/lookAt
        this.threeCamera.up.set(0, 0, 1);

        // Step 2: Position camera directly above the target point
        // X = longitude (east-west), Z = latitude (north-south), Y = height
        // CRITICAL FIX: Invert X-axis sign so floor moves in same direction as map
        // When map moves right (longitude increases), centerOffset.x is positive
        // But camera must move left (negative) so floor appears to move right
        // When map moves left (longitude decreases), centerOffset.x is negative
        // But camera must move right (positive) so floor appears to move left
        this.threeCamera.position.set(-centerOffset.x, 500, centerOffset.y);

        // Step 3: Look at the target point on the floor (X-Z plane at Y=0)
        // CRITICAL: Also invert X-axis for lookAt to match camera position
        // This aligns camera's local coordinate system with world coordinates
        this.threeCamera.lookAt(-centerOffset.x, 0, centerOffset.y);

        // Step 4: Set frustum BEFORE updating matrices
        // For orthographic camera, frustum defines view in camera's local coordinate system
        // After lookAt with up=(0,0,1):
        // - left/right = camera's local X = world X (longitude/east-west)
        // - top/bottom = camera's local Y = world Z (latitude/north-south)
        this.threeCamera.left = -sizeX;
        this.threeCamera.right = sizeX;
        this.threeCamera.top = sizeY;
        this.threeCamera.bottom = -sizeY;

        // Step 5: Update matrices in correct order
        // First update world matrix (applies position and rotation)
        this.threeCamera.updateMatrixWorld();
        // Then update projection matrix (applies frustum)
        this.threeCamera.updateProjectionMatrix();
      } else {
        // Fallback for PerspectiveCamera (shouldn't happen, but just in case)
        const fov = this.threeCamera.fov * (Math.PI / 180);
        const aspect = this.threeCamera.aspect;
        const verticalFov = 2 * Math.atan(Math.tan(fov / 2) * aspect);
        const distanceForHeight = (visibleHeight / 2) / Math.tan(verticalFov / 2);
        const distanceForWidth = (visibleWidth / 2) / Math.tan(fov / 2);
        const cameraDistance = Math.max(distanceForHeight, distanceForWidth) * 1.1;
        const clampedDistance = Math.max(10, Math.min(1000, cameraDistance));

        // CRITICAL: Invert X-axis for PerspectiveCamera too (same as OrthographicCamera)
        this.threeCamera.position.set(-centerOffset.x, clampedDistance, centerOffset.y);
        this.threeCamera.lookAt(-centerOffset.x, 0, centerOffset.y);
      }

      // CRITICAL: Update controls target to match current map center
      // This must be done AFTER camera position is set to ensure correct alignment
      // Both X and Z coordinates must be updated for proper sync
      // CRITICAL: Invert X-axis to match camera position (same as camera.position and lookAt)
      this.threeControls.target.set(-centerOffset.x, 0, centerOffset.y);

      // CRITICAL: Keep controls disabled after sync to prevent interference
      // Controls will be re-enabled by mouseMoveHandler when user hovers over 3D objects
      // This ensures floor stays synced with map during pan/zoom operations
      this.threeControls.enabled = false;

      // Update controls to apply the target change (must be after disabling)
      this.threeControls.update();

      // Force render to ensure changes are visible immediately
      if (this.threeRenderer && this.threeScene && this.threeCamera) {
        this.threeRenderer.render(this.threeScene, this.threeCamera);
      }
    }
    finally {
      // Ensure controls are disabled after sync to prevent interference
      if (this.threeControls) {
        this.threeControls.enabled = false;
      }
    }
  }

  /**
   * Cleanup Three.js resources
   */
  private cleanupThreeJs(): void {
    // Remove Leaflet sync listeners
    this.removeLeafletSyncListeners();
    // Remove room tooltip if present
    if (this.roomTooltip && this.floorMap) {
      this.floorMap.removeLayer(this.roomTooltip);
      this.roomTooltip = null;
    }

    // Clear users from Three.js scene
    this.clearUsersInThreeJs();

    // Remove resize listener
    const resizeHandler = this.onWindowResize.bind(this);
    window.removeEventListener('resize', resizeHandler);

    // Cancel animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Dispose controls
    if (this.threeControls) {
      this.threeControls.dispose();
      this.threeControls = null;
    }

    // Remove and dispose renderer
    if (this.threeRenderer) {
      const canvas = this.threeRenderer.domElement;

      // Remove hover handler if present
      const hoverHandler = (canvas as any)._floorMapMouseMoveHandler;
      if (hoverHandler) {
        canvas.removeEventListener('mousemove', hoverHandler);
        delete (canvas as any)._floorMapMouseMoveHandler;
      }

      // Remove mousedown handler if present
      const mouseDownHandler = (canvas as any)._floorMapMouseDownHandler;
      if (mouseDownHandler) {
        canvas.removeEventListener('mousedown', mouseDownHandler);
        delete (canvas as any)._floorMapMouseDownHandler;
      }

      // Remove click handler if present
      const clickHandler = (canvas as any)._floorMapClickHandler;
      if (clickHandler) {
        canvas.removeEventListener('click', clickHandler);
        delete (canvas as any)._floorMapClickHandler;
      }

      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
      this.threeRenderer.dispose();
      this.threeRenderer = null;
    }

    // Clean up scene
    if (this.threeScene) {
      // Dispose geometries and materials
      this.threeScene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((mat) => mat.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });

      while (this.threeScene.children.length > 0) {
        this.threeScene.remove(this.threeScene.children[0]);
      }
      this.threeScene = null;
    }

    this.threeCamera = null;
    this.threeRaycaster = null;
    this.threeJsInitialized = false;
    this.beaconOnChange(); // Restore Leaflet beacon visibility if enabled
  }

}
